import { useRef, useState, useEffect } from "react";
import { Box, ToggleButton, ToggleButtonGroup, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Typography } from "@mui/material";
import { resolveQuestionHtmlAssetUrl } from "../../../api";

/**
 * Shared interactive answer widget for "drawing" questions, graded deterministically (no AI).
 * Renders the question image with an SVG overlay (viewBox 0..100, fractional coords) and supports:
 *   - kind 'shade'       : tap region polygons to fill them. value = array of region ids.
 *   - kind 'points'      : tap grid intersections to place points (snap). value = array of [gx,gy].
 *   - kind 'drag-shape'  : drag a copy of the original shape, snapping to the grid. value = vertices.
 *   - kind 'rect'        : tap two opposite grid corners to draw an axis-aligned rectangle. value = [[x1,y1],[x2,y2]].
 *   - kind 'lines'       : tap two points (no grid) to draw each line; repeat for several. value = array of [[x1,y1],[x2,y2]].
 *
 * Same component is used by the student (editable), the verify/result reveal (readOnly + revealValue),
 * and the admin authoring editor (admin taps the correct answer = the value it stores as the target).
 */
const SHADE_FILL = "rgba(25,118,210,0.45)";
const REVEAL_FILL = "rgba(46,125,50,0.45)";
const REVEAL_STROKE = "#2e7d32";
const ACTIVE_STROKE = "#1976d2";
const GIVEN_FILL = "rgba(120,120,120,0.5)"; // pre-coloured "given" shade cells (locked, not tappable)

// Glyph symbols that look identical under any rotation or mirror — offering rotate/flip buttons for
// these is pure clutter since neither action changes the picture.
const ROTATION_INVARIANT_CHARS = new Set(["+", "○", "●", "•"]);

// Distinct fill/stroke pairs cycled across labelled region GROUPS (all regions sharing one `group`,
// or — when no group is authored — one colour per distinct `label` string) so a multi-region answer
// like a pie chart's activity wedges reads as visually separate captioned areas instead of one flat
// grey mass. Picked for contrast against both the grey GIVEN_FILL and each other.
const LABEL_GROUP_PALETTE = [
  { fill: "rgba(76,175,80,0.55)", stroke: "#2e7d32" },   // green
  { fill: "rgba(255,152,0,0.55)", stroke: "#e65100" },   // orange
  { fill: "rgba(156,39,176,0.5)", stroke: "#6a1b9a" },   // purple
  { fill: "rgba(3,169,244,0.5)", stroke: "#0277bd" },    // sky blue
  { fill: "rgba(233,30,99,0.5)", stroke: "#ad1457" },    // pink
  { fill: "rgba(205,220,57,0.55)", stroke: "#827717" },  // lime
  { fill: "rgba(0,150,136,0.5)", stroke: "#00695c" },    // teal
  { fill: "rgba(121,85,72,0.5)", stroke: "#4e342e" },    // brown
];

const eqPt = (a, b) => Number(a[0]) === Number(b[0]) && Number(a[1]) === Number(b[1]);

// Split a polygon (fractional coords) by the infinite line through a–b. Returns the two halves
// ordered [smaller-centroid-x first] or null when the line misses the polygon. Used by kind
// 'cut-drag': the student cuts the shape, then drags the pieces to reassemble them.
const splitPolyByLine = (poly, a, b) => {
  const side = (p) => (b[0] - a[0]) * (p[1] - a[1]) - (b[1] - a[1]) * (p[0] - a[0]);
  const neg = [], pos = [];
  const n = poly.length;
  for (let i = 0; i < n; i++) {
    const p = poly[i], q = poly[(i + 1) % n];
    const sp = side(p), sq = side(q);
    if (sp <= 1e-9) neg.push(p);
    if (sp >= -1e-9) pos.push(p);
    if ((sp > 1e-9 && sq < -1e-9) || (sp < -1e-9 && sq > 1e-9)) {
      const t = sp / (sp - sq);
      const ix = [p[0] + t * (q[0] - p[0]), p[1] + t * (q[1] - p[1])];
      neg.push(ix); pos.push(ix);
    }
  }
  if (neg.length < 3 || pos.length < 3) return null;
  const cx = (pts2) => pts2.reduce((s, p) => s + p[0], 0) / pts2.length;
  return cx(neg) <= cx(pos) ? [neg, pos] : [pos, neg];
};

// Pictogram icon shapes, keyed by spec.icon. Each has its own viewBox, on-screen aspect (h/w),
// path, and the x-extent of the "left half" (for the 0.5 clip). Default is the bottle.
const PICTO_ICONS = {
  bottle: { vb: "0 0 24 60", aspect: 2.2, halfW: 12,
    d: "M9 3 L15 3 L15 11 C15 13 16 14 18 16 C19.5 17.5 20 19 20 22 L20 52 C20 55 18 57 15 57 L9 57 C6 57 4 55 4 52 L4 22 C4 19 4.5 17.5 6 16 C8 14 9 13 9 11 Z" },
  lorry: { vb: "0 0 60 32", aspect: 0.52, halfW: 30,
    d: "M3 6 L36 6 L36 14 L46 14 L52 20 L52 25 L3 25 Z M8 25 A3 3 0 1 0 8.01 25 Z M44 25 A3 3 0 1 0 44.01 25 Z" },
  circle: { vb: "0 0 40 40", aspect: 1, halfW: 20, d: "M20 3 A17 17 0 1 1 19.99 3 Z" },
  star: { vb: "0 0 40 40", aspect: 1, halfW: 20,
    d: "M20 2 L25 15 L39 15 L28 24 L32 38 L20 30 L8 38 L12 24 L1 15 L15 15 Z" },
  square: { vb: "0 0 40 40", aspect: 1, halfW: 20, d: "M4 4 L36 4 L36 36 L4 36 Z" },
  // Arm/crossbar proportions traced from the ISEB sports-injury key so the drawn icon matches the
  // ones already printed in the paper's table.
  cross: { vb: "0 0 34 34", aspect: 1, halfW: 17,
    d: "M12 1 L22 1 L22 10 L33 10 L33 24 L22 24 L22 33 L12 33 L12 24 L1 24 L1 10 L12 10 Z" },
};
const pictoIcon = (key) => PICTO_ICONS[key] || PICTO_ICONS.bottle;
// Part-filled icons are drawn by quadrant, matching how the printed papers draw them: a half is the
// left half, a quarter the top-left quadrant, three-quarters everything but the bottom-right.
const PICTO_FILLS = [1, 0.75, 0.5, 0.25];
const PICTO_CYCLE = [0, 1, 0.75, 0.5, 0.25];
const pictoClipRects = (fill, halfW, halfH) => {
  if (fill === 0.25) return [[0, 0, halfW, halfH]];
  if (fill === 0.5) return [[0, 0, halfW, halfH * 2]];
  if (fill === 0.75) return [[0, 0, halfW, halfH * 2], [halfW, 0, halfW, halfH]];
  return null;
};

const InteractiveDiagramCanvas = ({
  spec,
  imageUrl,
  value,
  onChange,
  readOnly = false,
  revealValue = null,
  gridMovable = false,
  onMoveGrid = null,
  onToggleGiven = null, // editor-only: tap any shade region to flag/unflag it as pre-coloured "given"
  maxWidth = null, // cap the rendered width (px) so grids aren't huge on desktop; still full-width on mobile
  // Other sub-questions' committed work on a shared grid, drawn read-only in one colour per part so the
  // student can tell which part each mark belongs to. Each entry:
  // { id, label, color, points:[[gx,gy,lbl?]], edges:[[[gx,gy],[gx,gy]]], lines:[[[fx,fy],[fx,fy]]] }
  contextLayers = null,
}) => {
  const wrapRef = useRef(null);
  const uid = useRef(Math.random().toString(36).slice(2, 8)).current; // unique clipPath id prefix
  const [drag, setDrag] = useState(null); // { startX, startY, baseOffset:[ox,oy] }
  const [gridDrag, setGridDrag] = useState(null); // { lastX, lastY } while dragging the whole grid
  const [imgFailed, setImgFailed] = useState(false);
  const [usedRaw, setUsedRaw] = useState(false);
  const [pendingPt, setPendingPt] = useState(null); // first tapped endpoint of an in-progress line (kind 'lines')
  const [pendingNode, setPendingNode] = useState(null); // first tapped node index of an in-progress join (kind 'segments')
  const [box, setBox] = useState({ w: 0, h: 0 }); // rendered size, for sizing HTML glyph overlays in px
  const [selGlyph, setSelGlyph] = useState(null); // index of the glyph the single shared toolbar edits
  const [clockDrag, setClockDrag] = useState(null); // 'hour' | 'minute' while dragging a clock hand
  const [showConnect, setShowConnect] = useState(true); // reflect/rotate: join the placed corners with an outline
  const [manualFill, setManualFill] = useState(false); // reflect/rotate: colour the finished shape in
  // When labels are REQUIRED, tapping a point opens a modal that forces the student to type its label
  // (the inline on-grid box is too easy to miss — students plotted correctly but left it blank). Holds
  // { index, value, isNew } for the point being labelled; a blank confirm on a new point removes it.
  const [labelPrompt, setLabelPrompt] = useState(null);
  // "Label the triangle A": after the shape is complete, force a popup for the shape's letter (not a
  // corner). Holds { value } while open; empty means not prompting.
  const [shapeLabelPrompt, setShapeLabelPrompt] = useState(null);
  const shapeLabelPromptedRef = useRef(false);
  // Letter-entry cells ("write C in this section"): tapping the shape opens a forced popup for its
  // letter, same pattern as the point/shape label prompts above — an always-visible floating input
  // box was too easy to mis-attribute to the wrong region when several sit close together in one figure.
  // Holds { id, value } while open.
  const [letterPrompt, setLetterPrompt] = useState(null);
  // Multi-group shading: which category the next tapped region gets coloured as. Defaults to the first.
  const [activeGroup, setActiveGroup] = useState(null);
  // Manual edge drawing for transform/polygon shapes: auto-connect can't reconstruct a concave outline
  // (an L / arrow) from an unordered corner set, so the student draws each edge by hand. No mode toggle
  // — corners are always tappable handles: tap one (it highlights) then another to join them; tap an
  // empty grid spot to add a corner; tap a drawn edge to remove it. Edges are a VISUAL AID only; grading
  // still uses the corner set, so edges live in local state (undirected corner-coord keys) and never
  // change the submitted value.
  const [edgePending, setEdgePending] = useState(null); // index of the first tapped corner, or null
  const [manualEdgesLocal, setManualEdgesLocal] = useState([]); // ["gx,gy|gx,gy", ...] local mirror of value.edges
  // Source of truth for the armed corner during an event: state reads are stale if one tap reaches two
  // handlers in the same event (both would read the pre-tap value and the second call re-toggles,
  // undoing the first). The ref updates synchronously so a duplicate call is harmless; edgePending
  // state mirrors it purely for rendering the highlight.
  const edgePendingRef = useRef(null);

  const kind = spec?.kind || "shade";
  const regions = Array.isArray(spec?.regions) ? spec.regions : [];
  const grid = spec?.grid || null;
  const connect = spec?.connect !== false;
  const isRect = kind === "rect";
  const isPoly = kind === "polygon";
  const isGlyph = kind === "glyph";
  // reflect/rotate place the transformed shape's vertices exactly like 'points', with the original
  // shape + mirror line / rotation centre drawn as a guide overlay on top.
  const isReflect = kind === "reflect";
  const isRotate = kind === "rotate";
  const isTransform = isReflect || isRotate;
  // Kinds where the student places every corner first, then explicitly joins them and colours the
  // shape in with the toolbar (instead of the outline auto-drawing/auto-filling on each tap). This
  // keeps tap order — so a concave shape traced corner-by-corner fills cleanly — and never shows a
  // half-drawn bowtie mid-placement. reflect/rotate already worked this way; points/polygon join it.
  const isClock = kind === "clock";
  const isMarker = kind === "marker";
  // polygon draws a closed shape by tapping grid points, graded by area — renders like connected points.
  // The cap on how many points can be placed: point_count when authored, else (for reflect/rotate) the
  // number of vertices in the original shape — so a reflect can never receive an extra stray point even
  // if point_count was omitted from the spec.
  const pointCount = isRect ? 2
    : (Number(spec?.point_count) || (isTransform ? (Array.isArray(spec?.original_shape) ? spec.original_shape.length : 0) : 0));
  // A polygon question can ask for several separate closed shapes on one grid ("sketch the next two
  // rectangles"): shape_count>1 means the placed points are grouped into consecutive runs of
  // (point_count / shape_count) corners, each drawn/filled as its own quad rather than one big polygon.
  // Open 'points' plots use the same grouping to draw SEPARATE lines ("join K to L, then reflect that
  // line in p" = two 2-point lines): without it the four plotted points chain into one zig-zag.
  const shapeCount = (isPoly || kind === "points") ? Math.max(1, Math.round(Number(spec?.shape_count) || 1)) : 1;
  const minPerShape = spec?.closed === false ? 2 : 3;
  const cornersPerShape = shapeCount > 1 && pointCount > 0 ? Math.max(minPerShape, Math.round(pointCount / shapeCount)) : pointCount;
  const original = Array.isArray(spec?.original_shape) ? spec.original_shape : [];
  // Read-only guide points carried over from an earlier part of the same grouped question that shares
  // this grid (e.g. part b plotted A/B/C; part c reflects them on the SAME figure). Each entry is
  // [gx, gy, label?] in grid coords; drawn grey, never tappable, never graded.
  // given_points are corners already PRINTED on the diagram (kite ABCD's A and C, drawn on the paper).
  // The student joins to them but never plots them — same read-only, joinable treatment as a carried-over
  // context point, but authored on the question and part of the graded shape.
  const contextPoints = [
    ...(Array.isArray(spec?.given_points) ? spec.given_points : []),
    ...(Array.isArray(spec?.context_points) ? spec.context_points : []),
  ].filter((p) => Array.isArray(p) && p.length >= 2);
  const pointLabels = Array.isArray(spec?.point_labels) ? spec.point_labels : [];
  // reflect/rotate answers are just corner placements — labelled only when the spec opts in
  // (allow_labels: true), e.g. "reflect the shape and label the new shape B". Plot-point / drag-shape
  // questions keep labels on by default.
  const allowLabels = spec?.allow_labels === true || (!isTransform && spec?.allow_labels !== false);
  // Label is graded, not optional: tapping a point opens a forced label prompt instead of the easy-to-
  // miss inline box. Only for tappable point kinds (not transforms, which label the whole shape).
  const labelRequired = !!spec?.require_point_labels && allowLabels && (kind === "points" || kind === "drag-shape" || isPoly);
  // Whole-shape letter ("Label the triangle A" / "Label your shape B") — separate from corner labels.
  const requireShapeLabel = !!spec?.require_shape_label && (kind === "points" || kind === "drag-shape" || isPoly || isTransform);
  const closed = spec?.closed !== false;
  // Open lines anchored to a point already GIVEN on the diagram — e.g. "join K to L" where K is printed
  // and only L gets tapped. Tap-order chunking has no slot for a point that's never tapped, so this asks
  // for an explicit edge (tap K's dot, then L's) instead; required_edges is what the backend grades
  // against. Opt-in via required_edges so it never changes any existing open 'points' question — plenty
  // of those carry context/given points purely as decoration, with no line to join them into.
  // `required_edges` as an array (even []) is the opt-in signal, not its length: the admin authoring
  // editor starts one at [] the moment a given point is added, before any edge has been drawn yet, and
  // needs this mode live immediately in order to draw the first one.
  const joinContextEdges = kind === "points" && connect && !closed && contextPoints.length > 0
    && (Array.isArray(spec?.required_edges) || spec?.join_context_edges === true);
  // points/polygon that draw a closed shape get the manual Connect/Fill workflow too. Open plots
  // (connect:false, i.e. "just mark these points, tap order carries meaning") keep auto-connecting.
  const isManualFill = isTransform || ((isPoly || (kind === "points" && connect)) && closed) || joinContextEdges;
  // Manual edge-drawing is offered on single closed shapes where auto-ordering can go wrong (a concave
  // reflection/rotation/polygon). Multi-shape polygons ("sketch N rectangles") stay on tap-order
  // chunking — their corners split into separate quads, which hand-drawn edges can't express.
  const edgesAvailable = isManualFill && shapeCount === 1;
  // "Fill in" colours the enclosed region, which only exists for a closed shape — an open join-to-context
  // line (joinContextEdges) has no area, so it must not offer the toggle at all.
  const fillEligible = isManualFill && closed;
  const cornerKey = (p) => `${Math.round(Number(p[0]))},${Math.round(Number(p[1]))}`;
  const edgeKey = (a, b) => [cornerKey(a), cornerKey(b)].sort().join("|");
  const isLines = kind === "lines";
  const isLine = (l) => Array.isArray(l) && l.length >= 2 && Array.isArray(l[0]) && Array.isArray(l[1]);
  const placedLines = isLines && Array.isArray(value) ? value.filter(isLine) : [];
  const revealLines = isLines && Array.isArray(revealValue) ? revealValue.filter(isLine) : null;
  // cut-drag: the student cuts spec.shape (fractional polygon) with one straight line, the shape
  // splits into two pieces, and they drag a piece so the two reassemble into the target shape
  // (graded server-side on the pieces' relative displacement = the shape's base vector BL→BR).
  // value = { cut: [[x1,y1],[x2,y2]], offsets: [[dxL,dyL],[dxR,dyR]] } (offsets ordered left, right).
  const isCutDrag = kind === "cut-drag";
  const cdShape = isCutDrag && Array.isArray(spec?.shape)
    ? spec.shape.filter((p) => Array.isArray(p) && p.length >= 2).map((p) => [Number(p[0]), Number(p[1])])
    : [];
  const cdValue = isCutDrag && value && !Array.isArray(value) && Array.isArray(value.cut) && isLine(value.cut) ? value : null;
  const cdReveal = isCutDrag && revealValue && !Array.isArray(revealValue) && Array.isArray(revealValue.cut) && isLine(revealValue.cut) ? revealValue : null;
  const cdShow = cdReveal || cdValue;
  const cdPieces = cdShow && cdShape.length >= 3 ? splitPolyByLine(cdShape, cdShow.cut[0], cdShow.cut[1]) : null;
  const cdOffsets = cdShow && Array.isArray(cdShow.offsets) && cdShow.offsets.length >= 2 ? cdShow.offsets : [[0, 0], [0, 0]];
  const cdBase = cdShape.length >= 4 ? [cdShape[2][0] - cdShape[3][0], cdShape[2][1] - cdShape[3][1]] : [0, 0];
  const cdSnapTol = Number(spec?.snap_tol) || 0.05;
  // segments: fixed nodes (crosses) drawn on the image; the student joins pairs with straight lines.
  const isSegments = kind === "segments";
  const nodes = isSegments && Array.isArray(spec?.nodes) ? spec.nodes.filter((n) => Array.isArray(n) && n.length >= 2) : [];
  const placedSegs = isSegments && Array.isArray(value) ? value.filter(isLine) : [];
  const revealSegs = isSegments && Array.isArray(revealValue) ? revealValue.filter(isLine) : null;
  // pictogram: a row of icon slots the student fills in quarters (0 / 0.25 / 0.5 / 0.75 / 1). value = fills.
  const isPictogram = kind === "pictogram";
  const pictoRow = spec?.row || {};
  const pictoCount = Math.max(0, Math.round(Number(pictoRow.count) || 0));
  const normFill = (v) => (PICTO_FILLS.includes(v) ? v : 0);
  const pictoStates = isPictogram ? Array.from({ length: pictoCount }, (_, i) => normFill(Number(Array.isArray(value) ? value[i] : 0))) : [];
  const revealPicto = isPictogram && Array.isArray(revealValue) ? Array.from({ length: pictoCount }, (_, i) => normFill(Number(revealValue[i]))) : null;

  const src = imageUrl || spec?.image_url || "";
  const resolved = resolveQuestionHtmlAssetUrl(src) || src;
  const activeSrc = usedRaw ? src : resolved;

  useEffect(() => { setImgFailed(false); setUsedRaw(false); }, [src]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const update = () => setBox({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [src]);

  // Plain shade questions store value as an array of region-id strings. `require_labels` shade
  // questions (e.g. proportional pie sectors the student must shade AND caption) instead store
  // `[{id, label}, ...]` so each shaded region's typed text travels with it.
  const requireLabels = kind === "shade" && !!spec?.require_labels;
  // `label_only` shade questions: the picture already shows the correct shading/division (e.g. a pie
  // chart whose wedges are all pre-drawn) — every region is `given` and there is nothing to tap. The
  // student's only task is to type the caption for each labelled region, so label boxes show up
  // immediately (not gated behind shading a region first) for every `given` region that carries a
  // `spec.regions[].label`.
  const labelOnly = kind === "shade" && !!spec?.label_only;
  // Multi-group shading ("complete the pie chart"): the student picks a category, then taps regions to
  // colour them in that category's colour — working each category's SIZE out of a data table. Each
  // category gets its own palette colour so the finished chart reads as distinct labelled sectors.
  // value = [{id, group}, …]. spec.groups = [{key, label}] (the answer — how many regions each takes —
  // is stripped server-side).
  const shadeGroups = kind === "shade" && Array.isArray(spec?.groups) ? spec.groups.filter((g) => g && g.key) : [];
  const isGroupShade = shadeGroups.length > 0;
  const shadedEntries = Array.isArray(value) ? value : [];
  // `letter_regions`: regions that take a TYPED LETTER without being shaded ("write C in this section,
  // shade that one"). Their {id,label} entries live in the same value array but never count as shading.
  const letterRegionIds = new Set(
    (Array.isArray(spec?.letter_regions) ? spec.letter_regions : []).map((lr) => String(lr?.id))
  );
  // `char` is stripped from the student-facing spec; it survives only in the full reveal spec
  // (correct_interactive), where it supplies the letter the correct-solution canvas must show.
  const letterRegionChar = Object.fromEntries(
    (Array.isArray(spec?.letter_regions) ? spec.letter_regions : [])
      .filter((lr) => lr && lr.char != null)
      .map((lr) => [String(lr.id), String(lr.char)])
  );
  // Optional per-region authoring overrides (`color`, `size`) so a specific letter-entry cell can be
  // made visually distinct from the default blue box — e.g. when several regions in one figure need
  // different treatment and the shared group colour isn't enough to tell them apart at a glance.
  const letterRegionStyle = Object.fromEntries(
    (Array.isArray(spec?.letter_regions) ? spec.letter_regions : [])
      .filter((lr) => lr && (lr.color || lr.size))
      .map((lr) => [String(lr.id), { color: lr.color || null, size: Number(lr.size) || null }])
  );
  const idOf = (v) => String(v && typeof v === "object" ? v.id : v);
  // A letter region's entry is a typed annotation, not shading — keep it out of the shaded set so its
  // cell never fills and its tap never toggles.
  const shadedIds = shadedEntries.filter((v) => !letterRegionIds.has(idOf(v))).map(idOf);
  const shadeLabels = (requireLabels || labelOnly || letterRegionIds.size > 0)
    ? Object.fromEntries(shadedEntries.filter((v) => v && typeof v === "object").map((v) => [idOf(v), v.label || ""]))
    : {};
  // region id -> the group the student assigned it to (group-shade mode).
  const groupOfRegion = isGroupShade
    ? Object.fromEntries(shadedEntries.filter((v) => v && typeof v === "object" && v.group).map((v) => [idOf(v), String(v.group)]))
    : {};
  const groupColorByKey = Object.fromEntries(
    shadeGroups.map((g, i) => [String(g.key), LABEL_GROUP_PALETTE[i % LABEL_GROUP_PALETTE.length]])
  );
  const revealIds = Array.isArray(revealValue) ? revealValue.map(idOf) : null;
  // The reveal (correct answer) for group-shade carries its own assignment, so the solution renders
  // with the same per-category colours the student was working with.
  const revealGroupOfRegion = isGroupShade && Array.isArray(revealValue)
    ? Object.fromEntries(revealValue.filter((v) => v && typeof v === "object" && v.group).map((v) => [idOf(v), String(v.group)]))
    : {};
  // Transform/closed-polygon edge shapes now carry the student-drawn outline in the value so it can be
  // graded (the outline is the answer, not just the corners). Shape: { points:[[x,y]…], edges:["gx,gy|gx,gy"…] }.
  // Legacy answers are a bare point array (no edges) and still read correctly here. shape_label rides on
  // the same object when the question asks the student to name the finished shape ("Label triangle A").
  const valueHasEdges = value && !Array.isArray(value) && Array.isArray(value.points);
  const valueHasShapeLabel = value && !Array.isArray(value) && typeof value.shape_label === "string";
  const placedPts = valueHasEdges
    ? value.points.filter((v) => Array.isArray(v))
    : (Array.isArray(value) && value.every((v) => Array.isArray(v)) ? value : []);
  const currentShapeLabel = valueHasShapeLabel
    ? value.shape_label.trim()
    : "";
  // A reveal value may be a bare point array OR the { points, edges } object an edge-drawn shape submits
  // (the review page replays the student's own drawing through revealValue). Unwrap both; the edges are
  // drawn as the revealed outline below.
  const revealRaw = revealValue && !Array.isArray(revealValue) && Array.isArray(revealValue.points)
    ? revealValue.points
    : revealValue;
  const revealEdges = revealValue && !Array.isArray(revealValue) && Array.isArray(revealValue.edges)
    ? revealValue.edges
    : null;
  const revealPts = Array.isArray(revealRaw) && revealRaw.every((v) => Array.isArray(v)) ? revealRaw : null;
  // Point labels are drawn as crisp HTML overlays (below) whenever the inline input box / label chip is
  // NOT on screen — i.e. the correct-answer reveal and the read-only review. SVG <text> is stretched into
  // an unreadable smear by the non-uniform viewBox on a wide figure (a probability scale is ~10:1), which
  // is why the A/B/C letters never showed on the scale reveal. When this is on, the SVG label is skipped.
  const labelOverlayActive = !!revealPts || !!readOnly;
  const revealShapeLabel = revealValue && !Array.isArray(revealValue) && typeof revealValue.shape_label === "string"
    ? revealValue.shape_label.trim()
    : ((labelOverlayActive || readOnly) && typeof spec?.shape_label === "string" ? spec.shape_label.trim() : "");
  // Given corners count toward a fillable shape: "plot D and draw the parallelogram ABCD" has the student
  // place ONE point, so gating on placedPts alone would leave "Fill in" disabled forever.
  const canFill = placedPts.length + (edgesAvailable ? contextPoints.length : 0) >= 3;

  // manualEdges: for graded edge shapes it lives in the value (value.edges) so it round-trips and can be
  // graded; for anything else it's purely local visual state. setManualEdges routes writes to the right
  // place. emitEdgeValue writes the {points, edges} pair — corner changes go through it too so edges are
  // never lost when a corner is added/removed on a graded edge shape. shape_label is preserved whenever
  // the question requires naming the finished shape (or the student already typed one).
  const manualEdges = valueHasEdges && Array.isArray(value.edges) ? value.edges : manualEdgesLocal;
  const emitValue = (nextPoints, nextEdges = undefined, shapeLabel = undefined) => {
    if (!onChange) return;
    const keepLabel = shapeLabel !== undefined ? shapeLabel : currentShapeLabel;
    const needsObject = requireShapeLabel || keepLabel !== "" || nextEdges !== undefined || valueHasEdges;
    if (!needsObject) {
      onChange(nextPoints);
      return;
    }
    const out = { points: nextPoints };
    if (nextEdges !== undefined) out.edges = nextEdges;
    else if (valueHasEdges && Array.isArray(value.edges)) out.edges = value.edges;
    if (requireShapeLabel || keepLabel !== "") out.shape_label = keepLabel;
    onChange(out);
  };
  const emitEdgeValue = (nextPoints, nextEdges) => {
    emitValue(nextPoints, nextEdges);
  };
  const setManualEdges = (updater) => {
    const next = typeof updater === "function" ? updater(manualEdges) : updater;
    if (edgesAvailable) {
      emitEdgeValue(placedPts, next);
    } else {
      setManualEdgesLocal(next);
    }
  };
  const setShapeLabel = (label) => {
    emitValue(placedPts, valueHasEdges ? manualEdges : undefined, (label || "").trim());
  };

  // Drop the colour-fill again whenever there are too few corners to enclose a shape (a corner was
  // removed), so the student re-applies it after finishing.
  useEffect(() => { if (!canFill) setManualFill(false); }, [canFill]);

  // Commit the healed glyph value (see placedGlyphs above): rendering it corrected is not enough, because
  // an answer SUBMITTED without touching a letter would still carry the stale flipH+flipV and grade wrong.
  useEffect(() => {
    if (!isGlyph || readOnly || !onChange || !Array.isArray(value)) return;
    if (!value.some((g) => g && typeof g === "object" && g.flipH && g.flipV)) return;
    onChange(value.map((g) => (g && typeof g === "object" && g.flipH && g.flipV ? { ...g, flipH: false } : g)));
  }, [isGlyph, readOnly, onChange, value]);

  // ---- coordinate helpers (grid index <-> fractional 0..1) ----
  const gx0 = Number(grid?.x0 ?? 0.1), gy0 = Number(grid?.y0 ?? 0.1);
  const gdx = Number(grid?.dx ?? 0.1) || 0.1, gdy = Number(grid?.dy ?? 0.1) || 0.1;
  const minGridIntervals = kind === "points" ? 0 : 1;
  const cols = Math.max(minGridIntervals, Math.round(Number(grid?.cols ?? 8)));
  const rows = Math.max(minGridIntervals, Math.round(Number(grid?.rows ?? 8)));
  // Isometric (triangular / staggered) dot paper: odd rows are shifted right by half a column, so the
  // dots form the quincunx of triangular dotty paper. `fx` then depends on the row.
  const isoGrid = grid?.iso === true;
  // Diagonal (45°-rotated) squared paper: its printed intersections are the checkerboard half of a
  // square lattice of half the spacing — only cells whose (col + row) matches `parity` exist on the
  // paper. Without the mask half the dots would sit in the middle of a printed diamond.
  const gridParity = grid?.parity === 0 || grid?.parity === 1 ? Number(grid.parity) : null;
  const onParity = (gx, gy) => gridParity === null || Math.abs((gx + gy) % 2) === gridParity;
  const fx = (gx, gy) => gx0 + gx * gdx + (isoGrid && Math.abs(Math.round(Number(gy)) % 2) === 1 ? gdx / 2 : 0);
  const fy = (gy) => gy0 + gy * gdy;

  // reflect/rotate: the correct shape is the mirror/rotation of the (visible) original, so its outline
  // order is derivable here. We use it only to draw the student's placed corners in the right boundary
  // order when they colour the shape in — so a concave reflection fills cleanly no matter what order
  // the corners were tapped. It never affects grading (server-side, order-independent).
  const fracToGridPt = ([x, y]) => [(Number(x) - gx0) / gdx, (Number(y) - gy0) / gdy];
  const expectedTransformOrder = () => {
    if (!isTransform || original.length < 1) return [];
    if (isReflect) {
      if (!Array.isArray(spec?.mirror) || spec.mirror.length < 2) return [];
      const a = fracToGridPt(spec.mirror[0]), b = fracToGridPt(spec.mirror[1]);
      const dx = b[0] - a[0], dy = b[1] - a[1], dd = dx * dx + dy * dy || 1;
      return original.map(([px, py]) => {
        const t = ((px - a[0]) * dx + (py - a[1]) * dy) / dd;
        return [Math.round(2 * (a[0] + t * dx) - px), Math.round(2 * (a[1] + t * dy) - py)];
      });
    }
    if (!Array.isArray(spec?.center) || spec.center.length < 2) return [];
    const c = fracToGridPt(spec.center), ang = spec.angle || "cw90";
    return original.map(([px, py]) => {
      const dx = px - c[0], dy = py - c[1];
      const [nx, ny] = ang === "cw90" ? [-dy, dx] : ang === "ccw90" ? [dy, -dx] : [-dx, -dy];
      return [Math.round(c[0] + nx), Math.round(c[1] + ny)];
    });
  };
  // Reorder placed corners to follow the expected boundary sequence (nearest expected vertex first).
  const orderTransformPts = (placed) => {
    const exp = expectedTransformOrder();
    if (exp.length === 0 || placed.length === 0) return placed;
    const used = new Array(placed.length).fill(false);
    const out = [];
    exp.forEach((e) => {
      let bi = -1, bd = Infinity;
      placed.forEach((p, i) => {
        if (used[i]) return;
        const d = (Number(p[0]) - e[0]) ** 2 + (Number(p[1]) - e[1]) ** 2;
        if (d < bd) { bd = d; bi = i; }
      });
      if (bi >= 0) { used[bi] = true; out.push(placed[bi]); }
    });
    placed.forEach((p, i) => { if (!used[i]) out.push(p); });
    return out;
  };

  const clientToGrid = (clientX, clientY) => {
    const r = wrapRef.current?.getBoundingClientRect();
    if (!r || r.width === 0) return null;
    const fxv = (clientX - r.left) / r.width;
    const fyv = (clientY - r.top) / r.height;
    const gy = Math.round((fyv - gy0) / gdy);
    // Isometric dots: the row is snapped first, then the column is measured relative to that row's
    // half-column offset, so taps land on the visible staggered dots.
    const rowShift = isoGrid && Math.abs(gy % 2) === 1 ? gdx / 2 : 0;
    const gx = Math.round((fxv - gx0 - rowShift) / gdx);
    if (gx < 0 || gx > cols || gy < 0 || gy > rows) return null;
    if (!onParity(gx, gy)) {
      // Rounding landed on a cell the paper has no dot at. Take whichever of the four neighbours is
      // nearest the tap, measured in fractional image units so the x/y spacings weigh correctly.
      const near = [[gx - 1, gy], [gx + 1, gy], [gx, gy - 1], [gx, gy + 1]]
        .filter(([x, y]) => x >= 0 && x <= cols && y >= 0 && y <= rows)
        .sort((a, b) => Math.hypot(fx(a[0], a[1]) - fxv, fy(a[1]) - fyv) - Math.hypot(fx(b[0], b[1]) - fxv, fy(b[1]) - fyv))[0];
      return near || null;
    }
    return [gx, gy];
  };

  // ---- shade interaction ----
  const currentGroup = isGroupShade ? (activeGroup ?? String(shadeGroups[0].key)) : null;
  const toggleRegion = (id) => {
    if (readOnly || !onChange) return;
    const sid = String(id);
    // Never let a pre-coloured "given" region enter the student's selection.
    if (regions.some((rg) => String(rg.id) === sid && rg.given)) return;
    // A letter region takes a typed letter, not shading — tapping it opens the forced letter prompt
    // instead of toggling a shade.
    if (letterRegionIds.has(sid)) {
      const existing = shadedEntries.find((v) => idOf(v) === sid);
      setLetterPrompt({ id: sid, value: (existing && typeof existing === "object" ? existing.label : "") || "" });
      return;
    }
    // Multi-group: a tap paints the region with the ACTIVE category. Tapping a region already in that
    // category clears it; tapping one coloured as a DIFFERENT category re-assigns it (so a student
    // fixing a mis-sized sector just paints over it instead of having to clear it first).
    if (isGroupShade) {
      const existing = groupOfRegion[sid];
      if (existing === currentGroup) {
        onChange(shadedEntries.filter((v) => idOf(v) !== sid));
      } else if (existing) {
        onChange(shadedEntries.map((v) => (idOf(v) === sid ? { id: sid, group: currentGroup } : v)));
      } else {
        onChange([...shadedEntries, { id: sid, group: currentGroup }]);
      }
      return;
    }
    if (shadedIds.includes(sid)) {
      onChange(shadedEntries.filter((v) => idOf(v) !== sid));
    } else if (requireLabels) {
      onChange([...shadedEntries, { id: sid, label: "" }]);
    } else {
      onChange([...shadedEntries, sid]);
    }
  };

  // ---- typed label for a shaded region (require_labels shade questions) ----
  // For `label_only` questions the region was never shaded/added to the value (it's `given`, not
  // tappable) — so the first keystroke must ADD its {id, label} entry rather than only updating an
  // existing one, or the label would have nowhere to be stored.
  const setRegionLabel = (id, label) => {
    if (readOnly || !onChange) return;
    const sid = String(id);
    // Check the ENTRIES, not shadedIds — a letter region is excluded from shadedIds by design, and
    // checking there would append a fresh {id,label} on every keystroke.
    if (shadedEntries.some((v) => idOf(v) === sid)) {
      onChange(shadedEntries.map((v) => (v && typeof v === "object" && idOf(v) === sid ? { ...v, label } : v)));
    } else {
      onChange([...shadedEntries, { id: sid, label }]);
    }
  };

  // Assigns each labelled region a distinct colour so a multi-region answer (e.g. a pie chart's
  // several activity wedges) reads as visually separate captioned areas. Regions sharing the same
  // `spec.regions[].group` (or, lacking that, the same `label` text) share a colour; each distinct
  // group gets the next palette slot in first-seen order, cycling if there are more groups than colours.
  // Colour is per GROUP, not per labelled region: in a label_only pie chart only one slice of each
  // activity carries the caption, but every slice of that activity must share the group's colour (else
  // the run of uncaptioned slices stays grey and the wedge doesn't read as one area). Keyed by `group`
  // when authored, else by the region's own label/id.
  const groupKeyOf = (rg) => String(rg?.group ?? rg?.label ?? rg?.id ?? "");
  const labelGroupColorOf = (() => {
    const order = [];
    regions.forEach((rg) => {
      const k = groupKeyOf(rg);
      if (k !== "" && !order.includes(k)) order.push(k);
    });
    return (rg) => {
      const k = groupKeyOf(rg);
      const idx = order.indexOf(k);
      return idx < 0 ? null : LABEL_GROUP_PALETTE[idx % LABEL_GROUP_PALETTE.length];
    };
  })();

  // A region takes a typed/dropped caption. The student spec never carries the answer text, so it
  // marks these with `labelable` (see InteractiveDiagramGrading::interactiveStudentSpec); the admin
  // editor holds the full spec and still has the authored `label`.
  const isLabelable = (rg) => !!(rg?.labelable || rg?.label);
  // Candidate captions for the drag chips — a word bank. Published by the server as `label_options`
  // (answer-order-independent); in the admin editor, derived from the authored region labels.
  const labelOptions = (() => {
    if (Array.isArray(spec?.label_options) && spec.label_options.length) return spec.label_options;
    return Array.from(new Set(regions.filter((rg) => rg.label).map((rg) => rg.label)));
  })();

  // A region's points-array is already in fractional 0..1 image coords (no grid transform needed).
  const regionCentroid = (rg) => {
    if (rg.r != null) return [Number(rg.cx), Number(rg.cy)];
    const pts = rg.points || [];
    if (!pts.length) return [0.5, 0.5];
    return [
      pts.reduce((s, p) => s + Number(p[0]), 0) / pts.length,
      pts.reduce((s, p) => s + Number(p[1]), 0) / pts.length,
    ];
  };

  // ---- per-point typed label (3rd element of each [gx, gy, label]) ----
  const setPointLabel = (i, label) => {
    if (readOnly || !onChange) return;
    const next = placedPts.map((p, j) => (j === i ? [p[0], p[1], label] : p));
    // On an edge-drawing shape the value is { points, edges } — emitting the bare point array here wiped
    // the outline the student had just drawn every time they typed a corner's letter.
    emitValue(next, edgesAvailable ? manualEdges : undefined);
  };

  // ---- manual edge drawing (tap two corners to join them) ----
  // Corners you can JOIN are the student's placed points PLUS the read-only context points (A/B/C given
  // on the printed grid). "Plot D and draw the parallelogram ABCD" only asks the student to place ONE
  // point, so without this the three sides that touch a given corner could never be drawn and the shape
  // could never be closed. Context corners are joinable but never movable, removable, or graded — edges
  // are keyed by grid coordinate ("gx,gy|gx,gy"), so an edge to a given corner serialises like any other.
  const edgeCorners = edgesAvailable && contextPoints.length > 0
    ? [...placedPts, ...contextPoints]
    : placedPts;
  const isContextCorner = (i) => i >= placedPts.length;

  const setPendingBoth = (v) => { edgePendingRef.current = v; setEdgePending(v); };
  const toggleCornerForEdge = (existing) => {
    const pending = edgePendingRef.current;
    if (pending === null) {
      setPendingBoth(existing);
      return;
    }
    if (pending === existing) {
      setPendingBoth(null); // tapped the same corner again → cancel
      return;
    }
    if (!edgeCorners[pending] || !edgeCorners[existing]) {
      setPendingBoth(null);
      return;
    }
    const key = edgeKey(edgeCorners[pending], edgeCorners[existing]);
    // Add-only: an edge is removed by tapping the drawn line, never by re-committing its corner pair —
    // so a duplicated call within one event can't add-then-remove the edge it just drew.
    const nextEdges = manualEdges.includes(key) ? manualEdges : [...manualEdges, key];
    setManualEdges(() => nextEdges);
    setPendingBoth(null);
    // Edges-only "join the corners" questions (all corners printed/carried, none placed) never hit the
    // point-placement popup triggers — fire the forced shape-label popup on the final joined edge.
    const neededEdges = Number(spec?.required_edge_count) || 0;
    if (
      nextEdges !== manualEdges && neededEdges > 0 && nextEdges.length >= neededEdges
      && requireShapeLabel && !currentShapeLabel && !shapeLabelPromptedRef.current
    ) {
      shapeLabelPromptedRef.current = true;
      setShapeLabelPrompt({ value: currentShapeLabel });
    }
  };
  const removeCorner = (i) => {
    if (readOnly || !onChange) return;
    const goneKey = cornerKey(placedPts[i]);
    const nextPts = placedPts.filter((_, j) => j !== i);
    const nextEdges = manualEdges.filter((k) => !k.split("|").includes(goneKey));
    setPendingBoth(null);
    if (edgesAvailable) emitEdgeValue(nextPts, nextEdges); // atomic {points,edges} — never split into two emits
    else emitValue(nextPts);
    if (nextPts.length < 3) shapeLabelPromptedRef.current = false;
  };

  // Single deterministic tap handler for edge-drawing shapes (reflect/rotate/closed polygon). Six
  // earlier attempts routed corner joins through per-element SVG hit-circles and kept breaking on
  // z-order/pointer-events: horizontal/vertical joins failed while diagonals worked, depending on which
  // sibling happened to sit on the lattice line. This resolves the tap in JS with no dependence on
  // paint order — one capture rect on top of everything snaps the tap to a grid cell, then acts on the
  // cell's contents: an occupied cell → arm/join/cancel that corner; an empty cell → place a corner.
  // Orientation is irrelevant because nothing is hit-tested against overlapping siblings.
  const tapEdgeCanvas = (clientX, clientY) => {
    if (readOnly || !onChange) return;
    // Snap the tap to a grid cell FIRST, then decide by cell contents — never by a distance-to-corner
    // tolerance. A tolerance that was ~half a cell created a dead band exactly on the boundary between
    // two orthogonally-adjacent corners: a tap aimed at the target but landing past the midpoint
    // resolved to the ARMED corner and cancelled the join, so horizontal/vertical joins failed while
    // diagonals (corners farther apart, no boundary in the way) still worked. Snapping to the cell the
    // student actually tapped removes the ambiguity — any tap inside the target cell joins it.
    const g = clientToGrid(clientX, clientY);
    if (!g) return;
    // A given (context) corner is joinable too, so resolve the tap against placed AND context corners.
    const existing = edgeCorners.findIndex((p) => eqPt(p, g));
    if (existing >= 0) {
      toggleCornerForEdge(existing); // arm / join / cancel on an existing corner
      return;
    }
    // Edges-only questions ("join the three points"): every corner is already on the grid, so an
    // empty-cell tap must not plant a stray corner the grader will then reject.
    if (spec?.no_new_points) return;
    if (pointCount > 0 && placedPts.length >= pointCount) return;
    setPendingBoth(null);
    const next = [...placedPts, g];
    emitEdgeValue(next, manualEdges); // empty cell → place a new corner, keep edges
    // A graded-label question (e.g. "plot D and draw the parallelogram ABCD") still needs its edges,
    // so labelling runs alongside edge-drawing rather than replacing it: placing a corner opens the
    // label prompt, while taps on existing corners keep arming/joining edges. Re-label via the corner's
    // inline tag.
    if (labelRequired) {
      setLabelPrompt({ index: next.length - 1, value: "", isNew: true });
    } else if (requireShapeLabel && (pointCount <= 0 || next.length >= pointCount) && next.length + contextPoints.length >= 3) {
      shapeLabelPromptedRef.current = true;
      setShapeLabelPrompt({ value: currentShapeLabel });
    }
  };

  // ---- point interaction ----
  const tapGrid = (clientX, clientY) => {
    if (readOnly || !onChange) return;
    const g = clientToGrid(clientX, clientY);
    if (!g) return;
    const existing = placedPts.findIndex((p) => eqPt(p, g));
    // Always-on edge drawing (closed transform/polygon shapes): tapping an EXISTING corner wires an
    // edge — first tap arms it (highlights), second tap on a different corner joins them, a repeat tap
    // on the armed corner cancels. A corner is removed only via its inline control, never by a plain
    // tap, so building the outline never accidentally deletes a corner.
    if (edgesAvailable) {
      // ONE tap path for corners: the generous grid hit-circles route every corner tap here. (Earlier
      // versions layered a second per-corner hit circle on top — the two handlers either both fired,
      // re-toggling and undoing the join, or left a dead ring where neither fired.)
      if (existing >= 0) {
        toggleCornerForEdge(existing);
        return;
      }
      if (spec?.no_new_points) return;
      if (pointCount > 0 && placedPts.length >= pointCount) return;
      const nextPts = [...placedPts, g];
      emitValue(nextPts, manualEdges);
      if (labelRequired) {
        setLabelPrompt({ index: nextPts.length - 1, value: "", isNew: true });
      } else if (requireShapeLabel && (pointCount <= 0 || nextPts.length >= pointCount) && nextPts.length + contextPoints.length >= 3) {
        shapeLabelPromptedRef.current = true;
        setShapeLabelPrompt({ value: currentShapeLabel });
      }
      return;
    }
    if (existing >= 0) {
      // On a label-required question, tapping an existing point re-opens its label prompt (so a
      // mislabelled point can be corrected) rather than removing it. The prompt's "Remove" clears it.
      if (labelRequired) {
        setLabelPrompt({ index: existing, value: typeof placedPts[existing][2] === "string" ? placedPts[existing][2] : "", isNew: false });
        return;
      }
      // Removing a corner also drops any manual edges touching it.
      const goneKey = cornerKey(placedPts[existing]);
      setManualEdges((es) => es.filter((k) => !k.split("|").includes(goneKey)));
      setEdgePending(null);
      const nextPts = placedPts.filter((_, i) => i !== existing);
      emitValue(nextPts);
      if (nextPts.length < 3) shapeLabelPromptedRef.current = false;
      return;
    }
    // At the point limit, tapping a new spot must NOT silently drop an earlier point — the student
    // removes a point only by tapping it. Ignore the extra tap instead of replacing the oldest.
    if (pointCount > 0 && placedPts.length >= pointCount) return;
    // Manual-fill kinds keep tap order so a concave outline traced corner-by-corner joins and fills
    // in the sequence the student intends — the outline is only drawn when they hit "Connect corners",
    // so there is never a half-drawn bowtie mid-placement and no auto-fill on each tap.
    const next = [...placedPts, g];
    emitValue(next);
    // Force the label immediately on a graded-label question so the point is never left unlabelled.
    if (labelRequired) {
      setLabelPrompt({ index: next.length - 1, value: "", isNew: true });
    } else if (requireShapeLabel && (pointCount <= 0 || next.length >= pointCount) && next.length + contextPoints.length >= 3) {
      // Once every corner is down, force the shape-name popup (e.g. "Label the triangle A").
      shapeLabelPromptedRef.current = true;
      setShapeLabelPrompt({ value: currentShapeLabel });
    }
  };

  // ---- lines interaction (free tap, no grid snap) ----
  const clientToFrac = (clientX, clientY) => {
    const r = wrapRef.current?.getBoundingClientRect();
    if (!r || r.width === 0 || r.height === 0) return null;
    return [(clientX - r.left) / r.width, (clientY - r.top) / r.height];
  };
  const tapLine = (clientX, clientY) => {
    if (readOnly || !onChange) return;
    const f = clientToFrac(clientX, clientY);
    if (!f) return;
    if (!pendingPt) { setPendingPt(f); return; }
    onChange([...placedLines, [pendingPt, f]]);
    setPendingPt(null);
  };
  const removeLine = (i, e) => {
    e.stopPropagation();
    if (readOnly || !onChange) return;
    onChange(placedLines.filter((_, j) => j !== i));
  };
  const clearLastLine = () => {
    if (readOnly || !onChange) return;
    if (pendingPt) { setPendingPt(null); return; }
    onChange(placedLines.slice(0, -1));
  };

  // ---- cut-drag interaction (tap two points to cut, then drag the pieces) ----
  const tapCut = (clientX, clientY) => {
    if (readOnly || !onChange) return;
    const f = clientToFrac(clientX, clientY);
    if (!f) return;
    if (!pendingPt) { setPendingPt(f); return; }
    onChange({ cut: [pendingPt, f], offsets: [[0, 0], [0, 0]] });
    setPendingPt(null);
  };
  const removeCut = () => { if (readOnly || !onChange) return; setPendingPt(null); onChange(null); };
  const resetPieces = () => { if (readOnly || !onChange || !cdValue) return; onChange({ ...cdValue, offsets: [[0, 0], [0, 0]] }); };
  const onPiecePointerDown = (i) => (e) => {
    if (readOnly || !onChange || !cdValue) return;
    e.preventDefault();
    e.stopPropagation();
    setDrag({
      startX: e.clientX,
      startY: e.clientY,
      base: cdOffsets.map((o) => [Number(o?.[0]) || 0, Number(o?.[1]) || 0]),
      piece: i,
    });
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  // ---- segments interaction (join two fixed nodes) ----
  const sameSeg = (s, a, b) => (eqPt(s[0], a) && eqPt(s[1], b)) || (eqPt(s[0], b) && eqPt(s[1], a));
  const tapNode = (i) => {
    if (readOnly || !onChange) return;
    const node = nodes[i];
    if (!node) return;
    if (pendingNode === null) { setPendingNode(i); return; }
    if (pendingNode === i) { setPendingNode(null); return; }
    const a = nodes[pendingNode], b = node;
    const existing = placedSegs.findIndex((s) => sameSeg(s, a, b));
    if (existing >= 0) onChange(placedSegs.filter((_, j) => j !== existing));
    else onChange([...placedSegs, [a, b]]);
    setPendingNode(null);
  };
  const removeSeg = (i, e) => {
    e.stopPropagation();
    if (readOnly || !onChange) return;
    onChange(placedSegs.filter((_, j) => j !== i));
  };
  const clearLastSeg = () => {
    if (readOnly || !onChange) return;
    if (pendingNode !== null) { setPendingNode(null); return; }
    onChange(placedSegs.slice(0, -1));
  };

  // ---- pictogram interaction (cycle each icon: empty → full → ¾ → ½ → ¼ → empty) ----
  const cycleIcon = (i) => {
    if (readOnly || !onChange) return;
    const cur = Array.from({ length: pictoCount }, (_, j) => normFill(pictoStates[j]));
    cur[i] = PICTO_CYCLE[(PICTO_CYCLE.indexOf(cur[i]) + 1) % PICTO_CYCLE.length];
    onChange(cur);
  };

  // ---- clock interaction (drag hour + minute hands) ----
  const round4 = (n) => Math.round(n * 1e4) / 1e4;
  const clockCenter = Array.isArray(spec?.center) && spec.center.length >= 2 ? [Number(spec.center[0]), Number(spec.center[1])] : [0.5, 0.5];
  const clockRadius = Number(spec?.radius) || 0.4;
  const HAND_LEN = { hour: clockRadius * 0.55, minute: clockRadius * 0.92 };
  const defaultHands = [[clockCenter[0], clockCenter[1] - HAND_LEN.hour], [clockCenter[0], clockCenter[1] - HAND_LEN.minute]];
  const clockHands = isClock && Array.isArray(value) && value.length >= 2 && Array.isArray(value[0]) && Array.isArray(value[1]) ? value : null;
  const revealHands = isClock && Array.isArray(revealValue) && revealValue.length >= 2 && Array.isArray(revealValue[0]) ? revealValue : null;
  const setHand = (which, clientX, clientY) => {
    if (readOnly || !onChange) return;
    const f = clientToFrac(clientX, clientY);
    if (!f) return;
    let dx = f[0] - clockCenter[0], dy = f[1] - clockCenter[1];
    const L = Math.hypot(dx, dy) || 1;
    const r = HAND_LEN[which];
    const ep = [round4(clockCenter[0] + (dx / L) * r), round4(clockCenter[1] + (dy / L) * r)];
    const cur = clockHands ? [clockHands[0], clockHands[1]] : [defaultHands[0], defaultHands[1]];
    cur[which === "hour" ? 0 : 1] = ep;
    onChange(cur);
  };
  const startHand = (which) => (e) => {
    if (readOnly || !onChange) return;
    e.preventDefault(); e.stopPropagation();
    setClockDrag(which);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  // ---- marker interaction (tap a value on a number line / scale / bar axis) ----
  const markerAxis = Array.isArray(spec?.axis) && spec.axis.length >= 2 && Array.isArray(spec.axis[0]) && Array.isArray(spec.axis[1]) ? spec.axis : null;
  const markerStyle = spec?.style === "bar" ? "bar" : "arrow";
  const markerBarWidth = Number(spec?.bar_width) || 0.06;
  const projectToAxis = (f) => {
    const [a, b] = markerAxis;
    const dx = b[0] - a[0], dy = b[1] - a[1];
    const l2 = dx * dx + dy * dy || 1;
    let t = ((f[0] - a[0]) * dx + (f[1] - a[1]) * dy) / l2;
    t = Math.max(0, Math.min(1, t));
    return [round4(a[0] + t * dx), round4(a[1] + t * dy)];
  };
  const tapMarker = (clientX, clientY) => {
    if (readOnly || !onChange || !markerAxis) return;
    const f = clientToFrac(clientX, clientY);
    if (!f) return;
    onChange([projectToAxis(f)]);
  };
  const markerPt = isMarker && Array.isArray(value) && Array.isArray(value[0]) ? value[0] : null;
  const revealMarkerPt = isMarker && Array.isArray(revealValue) && Array.isArray(revealValue[0]) ? revealValue[0] : null;

  // ---- glyph interaction (place / type / rotate / flip letters) ----
  // An answer placed before the flips became mutually exclusive can still carry BOTH — which is a 180°
  // rotation, not a mirror, and grades as wrong. For a symmetric letter (O, W) that is invisible on screen,
  // so the student sees a picture identical to the model answer and no reason for the mark. Collapse the
  // stale combination to the single mirror it was meant to be, so an old answer heals instead of silently
  // failing. (Deliberate rotations use `rot`, which is untouched.)
  const placedGlyphs = isGlyph && Array.isArray(value)
    ? value
      .filter((g) => g && typeof g === "object" && !Array.isArray(g))
      .map((g) => (g.flipH && g.flipV ? { ...g, flipH: false } : g))
    : [];
  const revealGlyphs = isGlyph && Array.isArray(revealValue) ? revealValue.filter((g) => g && typeof g === "object" && !Array.isArray(g)) : null;
  const snapFrac = (fx, fy) => {
    if (!grid) return [Math.round(fx * 1e4) / 1e4, Math.round(fy * 1e4) / 1e4];
    const gx = Math.round((fx - gx0) / gdx), gy = Math.round((fy - gy0) / gdy);
    return [Math.round((gx0 + gx * gdx) * 1e4) / 1e4, Math.round((gy0 + gy * gdy) * 1e4) / 1e4];
  };
  const addGlyph = (clientX, clientY) => {
    if (readOnly || !onChange) return;
    const f = clientToFrac(clientX, clientY);
    if (!f) return;
    const [x, y] = snapFrac(f[0], f[1]);
    onChange([...placedGlyphs, { char: "", pos: [x, y], flipH: false, flipV: false, rot: 0 }]);
    setSelGlyph(placedGlyphs.length);
  };
  const updateGlyph = (i, patch) => { if (readOnly || !onChange) return; onChange(placedGlyphs.map((g, j) => (j === i ? { ...g, ...patch } : g))); };
  const removeGlyph = (i) => { if (readOnly || !onChange) return; onChange(placedGlyphs.filter((_, j) => j !== i)); setSelGlyph(null); };

  // Placing a letter by tapping is hard to do precisely — "reflect the word SOW" needs each letter within
  // pos_tol of its mirrored position, and the tap lands wherever the finger lands. So a placed letter can
  // be DRAGGED to nudge it into place (and moved with the arrow keys, for fine adjustment). Dragging only
  // rewrites `pos`, so the typed character, rotation and flips all survive the move.
  const dragGlyph = (i, e) => {
    if (readOnly || !onChange) return;
    e.stopPropagation();
    e.preventDefault();
    setSelGlyph(i);
    const el = e.currentTarget;
    el.setPointerCapture?.(e.pointerId);
    let moved = false;
    const move = (ev) => {
      const f = clientToFrac(ev.clientX, ev.clientY);
      if (!f) return;
      moved = true;
      const [x, y] = snapFrac(f[0], f[1]);
      updateGlyph(i, { pos: [x, y] });
    };
    const up = (ev) => {
      el.releasePointerCapture?.(ev.pointerId);
      el.removeEventListener("pointermove", move);
      el.removeEventListener("pointerup", up);
      el.removeEventListener("pointercancel", up);
      // A tap that never moved is a plain select — leave the position untouched.
      if (!moved) setSelGlyph(i);
    };
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerup", up);
    el.addEventListener("pointercancel", up);
  };
  // Arrow keys nudge the selected letter by one grid step (or 0.5% of the image on a gridless figure).
  const nudgeGlyph = (i, dx, dy) => {
    if (readOnly || !onChange) return;
    const g = placedGlyphs[i];
    if (!g) return;
    const stepX = grid ? gdx : 0.005;
    const stepY = grid ? gdy : 0.005;
    const x = Math.max(0, Math.min(1, (g.pos?.[0] ?? 0) + dx * stepX));
    const y = Math.max(0, Math.min(1, (g.pos?.[1] ?? 0) + dy * stepY));
    updateGlyph(i, { pos: [Math.round(x * 1e4) / 1e4, Math.round(y * 1e4) / 1e4] });
  };
  // The glyph the toolbar edits. ONLY an explicitly selected letter — it used to fall back to the last
  // placed one, so the toolbar was permanently on screen (a floating bar at the bottom of the canvas) even
  // when nothing was selected. It now appears beside the letter you click and disappears when you click
  // away, which is what a selection actually means.
  const activeGlyph = selGlyph != null && selGlyph < placedGlyphs.length ? selGlyph : null;

  // ---- drag-shape interaction ----
  const placedShape = placedPts.length ? placedPts : original;
  const onShapePointerDown = (e) => {
    if (readOnly || !onChange || kind !== "drag-shape") return;
    e.preventDefault();
    setDrag({ startX: e.clientX, startY: e.clientY, base: placedShape });
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (clockDrag) { setHand(clockDrag, e.clientX, e.clientY); return; }
    if (gridDrag && onMoveGrid) {
      const r = wrapRef.current?.getBoundingClientRect();
      if (!r || r.width === 0) return;
      onMoveGrid((e.clientX - gridDrag.lastX) / r.width, (e.clientY - gridDrag.lastY) / r.height);
      setGridDrag({ lastX: e.clientX, lastY: e.clientY });
      return;
    }
    if (!drag) return;
    const r = wrapRef.current?.getBoundingClientRect();
    if (!r) return;
    // cut-drag: move one piece freely (fractional, no grid), snapping to "home" and to the
    // assembled position (relative displacement between the pieces = the shape's base vector).
    if (drag.piece != null) {
      if (!cdValue) return;
      const i = drag.piece, j = 1 - i;
      let off = [
        drag.base[i][0] + (e.clientX - drag.startX) / r.width,
        drag.base[i][1] + (e.clientY - drag.startY) / r.height,
      ];
      const assembled = i === 0
        ? [drag.base[j][0] + cdBase[0], drag.base[j][1] + cdBase[1]]
        : [drag.base[j][0] - cdBase[0], drag.base[j][1] - cdBase[1]];
      for (const c of [[0, 0], assembled]) {
        if (Math.hypot(off[0] - c[0], off[1] - c[1]) <= cdSnapTol) { off = c; break; }
      }
      const next = drag.base.map((o, k) => (k === i ? [Math.round(off[0] * 1e4) / 1e4, Math.round(off[1] * 1e4) / 1e4] : o));
      onChange({ ...cdValue, offsets: next });
      return;
    }
    const dgx = Math.round(((e.clientX - drag.startX) / r.width) / gdx);
    const dgy = Math.round(((e.clientY - drag.startY) / r.height) / gdy);
    const moved = drag.base.map(([x, y]) => [x + dgx, y + dgy]);
    emitValue(moved);
  };
  const onPointerUp = () => { setDrag(null); setGridDrag(null); setClockDrag(null); };

  // ---- whole-grid drag (admin authoring only) ----
  const onGridDragStart = (e) => {
    if (!gridMovable || !onMoveGrid) return;
    e.preventDefault();
    setGridDrag({ lastX: e.clientX, lastY: e.clientY });
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const pts = (arr) => arr.map(([x, y]) => `${fx(x, y) * 100},${fy(y) * 100}`).join(" ");
  const rectCorners = (a, b) => {
    const x0 = Math.min(a[0], b[0]), x1 = Math.max(a[0], b[0]);
    const y0 = Math.min(a[1], b[1]), y1 = Math.max(a[1], b[1]);
    return [[x0, y0], [x1, y0], [x1, y1], [x0, y1]];
  };

  if (!src || imgFailed) {
    return (
      <Box sx={{ p: 2, border: "1px dashed", borderColor: "divider", borderRadius: 1, color: "text.secondary", fontSize: "0.85rem" }}>
        This interactive question needs its diagram image. Please ask your tutor to re-add the image.
      </Box>
    );
  }

  return (
    // Block-level with a definite width so the wrapper's `min(100%, …px)` cap resolves against the
    // container (not the image's intrinsic width). align-items:flex-start keeps children hugging their
    // content (no stretch), so the wrapper stays exactly the image size and the SVG overlay aligns.
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.75, width: "100%", maxWidth: "100%" }}>
      {isCutDrag && !readOnly && cdValue && (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <ToggleButton
            size="small" value="removecut" selected={false} onChange={removeCut}
            sx={{ textTransform: "none", fontSize: "0.72rem", py: 0.25, px: 1.25, lineHeight: 1.2 }}
          >
            Remove cut
          </ToggleButton>
          <ToggleButton
            size="small" value="resetpieces" selected={false} onChange={resetPieces}
            sx={{ textTransform: "none", fontSize: "0.72rem", py: 0.25, px: 1.25, lineHeight: 1.2 }}
          >
            Reset pieces
          </ToggleButton>
        </Box>
      )}
      {isManualFill && !readOnly && (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          {!edgesAvailable && (
            <ToggleButton
              size="small" value="connect" selected={showConnect} disabled={placedPts.length < 2}
              onChange={() => setShowConnect((v) => !v)}
              sx={{ textTransform: "none", fontSize: "0.72rem", py: 0.25, px: 1.25, lineHeight: 1.2 }}
            >
              Connect corners
            </ToggleButton>
          )}
          {fillEligible && (
            <ToggleButton
              size="small" value="fill" selected={manualFill} disabled={!canFill}
              onChange={() => setManualFill((v) => {
                const next = !v;
                if (next && requireShapeLabel && !currentShapeLabel && !shapeLabelPromptedRef.current) {
                  shapeLabelPromptedRef.current = true;
                  setShapeLabelPrompt({ value: "" });
                }
                return next;
              })}
              sx={{ textTransform: "none", fontSize: "0.72rem", py: 0.25, px: 1.25, lineHeight: 1.2 }}
            >
              Fill in
            </ToggleButton>
          )}
          {edgesAvailable && manualEdges.length > 0 && (
            <ToggleButton
              size="small" value="clearedges" selected={false}
              onChange={() => { setManualEdges([]); setEdgePending(null); }}
              sx={{ textTransform: "none", fontSize: "0.72rem", py: 0.25, px: 1.25, lineHeight: 1.2 }}
            >
              Clear edges
            </ToggleButton>
          )}
          {edgesAvailable && (
            <Box component="span" sx={{ fontSize: "0.72rem", color: ACTIVE_STROKE, fontWeight: 600 }}>
              {edgePending === null
                ? "Tap two dots on empty grid to place corners, then tap one corner and another to draw an edge between them."
                : "Now tap the corner you want to join it to — or tap the × to delete this corner, or tap it again to cancel."}
            </Box>
          )}
        </Box>
      )}
      <Box
        ref={wrapRef}
        sx={{ position: "relative", display: "inline-block", maxWidth: maxWidth ? `min(100%, ${maxWidth}px)` : "100%", lineHeight: 0, touchAction: "none", userSelect: "none" }}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
      <img
        src={activeSrc}
        alt="Question diagram"
        style={{ maxWidth: "100%", display: "block" }}
        onError={() => {
          if (!usedRaw && resolved !== src && src) setUsedRaw(true);
          else setImgFailed(true);
        }}
      />
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", overflow: "visible" }}
      >
        {/* SHADE regions — polygons (points[]) or circles (cx/cy/r, e.g. a tap-to-fill circle lattice) */}
        {kind === "shade" && regions.map((rg) => {
          const id = String(rg.id);
          // `given` regions are pre-coloured context (already shaded in the picture) — always shown
          // filled, never tappable, and not part of the student's selection.
          const given = !!rg.given;
          const givenMode = !!onToggleGiven; // editor is flagging pre-coloured cells
          const isShaded = given || (revealIds ? revealIds.includes(id) : shadedIds.includes(id));
          // Multi-group: the colour comes from the category the student (or, on reveal, the mark
          // scheme) assigned this region to — that's what makes each sector read as its own activity.
          // A `given` region stays plain grey: it's the worked example already printed in the picture.
          const assignedGroup = isGroupShade && !given
            ? (revealIds ? revealGroupOfRegion[id] : groupOfRegion[id])
            : null;
          const groupColor = assignedGroup ? groupColorByKey[assignedGroup] : null;
          const fill = isShaded
            ? (given ? GIVEN_FILL : (groupColor?.fill || (revealIds ? REVEAL_FILL : SHADE_FILL)))
            : "rgba(0,0,0,0)";
          const stroke = groupColor?.stroke || (revealIds ? REVEAL_STROKE : ACTIVE_STROKE);
          // In given-mode every region stays tappable (so the admin can toggle the flag on/off);
          // otherwise a given region is locked and inert for the student — except in `label_only`
          // mode, where a labelled given region still accepts a dropped label chip directly on the
          // wedge (as well as on its floating label box).
          const locked = given && !givenMode;
          const dropTarget = labelOnly && locked && isLabelable(rg);
          const common = {
            fill,
            stroke,
            strokeWidth: 0.4,
            vectorEffect: "non-scaling-stroke",
            style: {
              cursor: (readOnly || (locked && !dropTarget)) ? "default" : "pointer",
              pointerEvents: (locked && !dropTarget) ? "none" : "all",
            },
            onClick: locked ? undefined : () => (givenMode ? onToggleGiven(id) : toggleRegion(id)),
            ...(dropTarget ? {
              onDragOver: (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; },
              onDrop: (e) => {
                e.preventDefault();
                const dragged = e.dataTransfer.getData("text/plain");
                if (dragged) setRegionLabel(id, dragged);
              },
            } : {}),
          };
          return rg.r != null ? (
            <circle key={id} cx={Number(rg.cx) * 100} cy={Number(rg.cy) * 100} r={Number(rg.r) * 100} {...common} />
          ) : (
            <polygon key={id} points={(rg.points || []).map(([x, y]) => `${x * 100},${y * 100}`).join(" ")} {...common} />
          );
        })}

        {/* POINTS / DRAG / RECT grid dots — visible dot scales with grid density so dense grids
            (e.g. 20×20 coordinate axes) stay clean; a larger invisible hit circle keeps tapping easy. */}
        {(kind === "points" || kind === "drag-shape" || isRect || isPoly || isTransform) && !readOnly && grid && (() => {
          const tappable = kind === "points" || isRect || isPoly || isTransform;
          const spacingU = Math.min(gdx, gdy) * 100;
          const visR = Math.max(0.45, Math.min(1.2, spacingU * 0.13));
          const hitR = Math.max(visR, spacingU * 0.45);
          return Array.from({ length: rows + 1 }).flatMap((_, j) =>
            Array.from({ length: cols + 1 }).map((__, i) => (!onParity(i, j) ? null : (
              <g key={`g${i}-${j}`}>
                {tappable && !edgesAvailable && (
                  <circle cx={fx(i, j) * 100} cy={fy(j) * 100} r={hitR} fill="rgba(0,0,0,0)"
                    style={{ cursor: "pointer", pointerEvents: "all" }}
                    onClick={(e) => tapGrid(e.clientX, e.clientY)} />
                )}
                <circle cx={fx(i, j) * 100} cy={fy(j) * 100} r={tappable ? visR : 0.7}
                  fill={tappable ? "rgba(25,118,210,0.30)" : "rgba(0,0,0,0.18)"}
                  style={{ pointerEvents: "none" }} />
              </g>
            )))
          );
        })()}

        {/* EDGE-DRAWING capture layer (reflect/rotate/closed polygon, student-editable). One rect over
            the whole canvas resolves every tap in JS (tapEdgeCanvas): snap to a grid cell, then
            occupied cell → arm/join, empty cell → place. It replaces the per-dot grid hit-circles
            (disabled above when edgesAvailable) so corner joins never depend on SVG paint order — the
            cause of horizontal/vertical joins failing while diagonals worked. The armed corner's × and
            the edge-remove hit-lines render AFTER this rect (later siblings paint on top), so those
            taps still win over the capture. */}
        {edgesAvailable && !readOnly && grid && (
          <rect x={0} y={0} width={100} height={100} fill="rgba(0,0,0,0)"
            style={{ cursor: "pointer", pointerEvents: "all" }}
            onClick={(e) => tapEdgeCanvas(e.clientX, e.clientY)} />
        )}

        {/* RECT rectangle (placed + reveal) */}
        {isRect && (() => {
          const show = revealPts && revealPts.length >= 2 ? revealPts : placedPts;
          const stroke = revealPts ? REVEAL_STROKE : ACTIVE_STROKE;
          const fillPoly = revealPts ? REVEAL_FILL : "rgba(25,118,210,0.20)";
          return (
            <>
              {show.length >= 2 && (
                <polygon points={pts(rectCorners(show[0], show[1]))} fill={fillPoly} stroke={stroke} strokeWidth={0.6} vectorEffect="non-scaling-stroke" />
              )}
              {show.slice(0, 2).map(([x, y], i) => (
                <circle key={`rc${i}`} cx={fx(x, y) * 100} cy={fy(y) * 100} r={2} fill={stroke} style={{ pointerEvents: "none" }} />
              ))}
              {/* First corner placed, second still pending — a small "1 of 2" badge clarifies that
                  one more tap (the opposite corner) completes the rectangle. */}
              {!readOnly && show.length === 1 && (
                <text x={fx(show[0][0], show[0][1]) * 100 + 2.5} y={fy(show[0][1]) * 100 - 2.5} fontSize="3.2" fill={stroke} style={{ pointerEvents: "none" }}>
                  1 of 2
                </text>
              )}
            </>
          );
        })()}

        {/* original shape ghost (drag-shape) */}
        {kind === "drag-shape" && original.length >= 2 && (
          <polygon points={pts(original)} fill="rgba(0,0,0,0.05)" stroke="#999" strokeWidth={0.4} strokeDasharray="2 2" vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
        )}

        {/* reflect / rotate guide: the shape to transform (solid dark outline) + the mirror line
            (reflect) or rotation centre (rotate). The student places the transformed vertices as points. */}
        {/* All transform guides are tap-transparent: they render AFTER the grid hit-circles, and SVG's
            default visiblePainted pointer-events would let their painted fill/stroke swallow taps on
            every intersection they cover (the 45° mirror passes exactly through lattice points, which
            blocked placing/joining the corners under it). */}
        {isTransform && original.length >= 2 && (
          <polygon points={pts(original)} fill="rgba(0,0,0,0.06)" stroke="#333" strokeWidth={0.6} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
        )}
        {isReflect && Array.isArray(spec?.mirror) && spec.mirror.length >= 2 && (() => {
          const [a, b] = spec.mirror;
          const dx = b[0] - a[0], dy = b[1] - a[1];
          const ex = a[0] - dx * 2, ey = a[1] - dy * 2, fx2 = b[0] + dx * 2, fy2 = b[1] + dy * 2;
          return <line x1={ex * 100} y1={ey * 100} x2={fx2 * 100} y2={fy2 * 100} stroke="#c62828" strokeWidth={0.6} strokeDasharray="2.5 2" vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />;
        })()}
        {isRotate && Array.isArray(spec?.center) && spec.center.length >= 2 && (() => {
          const cx = spec.center[0] * 100, cy = spec.center[1] * 100;
          return (
            <g style={{ pointerEvents: "none" }}>
              <line x1={cx - 2} y1={cy} x2={cx + 2} y2={cy} stroke="#c62828" strokeWidth={0.6} vectorEffect="non-scaling-stroke" />
              <line x1={cx} y1={cy - 2} x2={cx} y2={cy + 2} stroke="#c62828" strokeWidth={0.6} vectorEffect="non-scaling-stroke" />
            </g>
          );
        })()}

        {/* context guide: points/shape carried over from an earlier part sharing this grid (grey, inert).
            Drawn as a light outline when ≥3 corners (so the triangle from a plot-part shows on the
            reflect-part) plus a labelled dot per point. Never interactive, never graded. */}
        {contextPoints.length > 0 && (kind === "points" || kind === "drag-shape" || isPoly || isTransform) && (
          <g>
            {/* The auto-drawn guide outline is suppressed on an edge-drawing question: joining the corners
                IS the task ("draw the parallelogram ABCD"), so pre-drawing A→B→C both gives away part of
                the answer and reads as a shape the student didn't draw. */}
            {contextPoints.length >= 3 && !edgesAvailable && (
              <polygon points={pts(contextPoints)} fill="rgba(0,0,0,0.05)" stroke="#8a8a8a" strokeWidth={0.5} strokeDasharray="2 1.5" vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
            )}
            {contextPoints.map((pt, i) => {
              const [x, y] = pt;
              const lbl = typeof pt[2] === "string" ? pt[2] : "";
              // Given corners are joinable when edges are in play, so they get the same armed ring as a
              // placed corner. They are never removable — no × — since the question supplied them.
              const isPend = edgesAvailable && edgePending === placedPts.length + i;
              // A 4th element carries the colour of the sub-question that plotted this point on a shared
              // grid, so the student can tell whose mark it is. Absent -> the usual inert grey.
              const own = typeof pt[3] === "string" && pt[3] ? pt[3] : null;
              return (
                <g key={`ctx${i}`} style={{ pointerEvents: "none" }}>
                  {isPend && <circle cx={fx(x, y) * 100} cy={fy(y) * 100} r={3.4} fill="none" stroke="#0d47a1" strokeWidth={1} vectorEffect="non-scaling-stroke" />}
                  <circle cx={fx(x, y) * 100} cy={fy(y) * 100} r={isPend ? 2.4 : 1.7} fill={isPend ? "#0d47a1" : (own || "#8a8a8a")} />
                  {lbl ? (
                    <text x={fx(x, y) * 100 + 1.6} y={fy(y) * 100 - 1.6} fontSize={3.4} fontWeight="700" fill={isPend ? "#0d47a1" : (own || "#6a6a6a")}>{lbl}</text>
                  ) : null}
                </g>
              );
            })}
          </g>
        )}

        {/* Work committed on OTHER parts of a shared grid, one colour per part. Read-only guides: every
            element sets pointerEvents none, or they swallow taps on the grid hit-circles underneath. */}
        {Array.isArray(contextLayers) && contextLayers.length > 0 && (
          <g style={{ pointerEvents: "none" }}>
            {contextLayers.map((layer, li) => {
              const c = layer?.color || "#8a8a8a";
              return (
                <g key={`cl${li}`} opacity={0.7}>
                  {(Array.isArray(layer?.edges) ? layer.edges : []).map((e, i) => (
                    <line key={`cle${i}`}
                      x1={fx(e[0][0], e[0][1]) * 100} y1={fy(e[0][1]) * 100}
                      x2={fx(e[1][0], e[1][1]) * 100} y2={fy(e[1][1]) * 100}
                      stroke={c} strokeWidth={1} vectorEffect="non-scaling-stroke" />
                  ))}
                  {(Array.isArray(layer?.lines) ? layer.lines : []).map((l, i) => (
                    <line key={`cll${i}`}
                      x1={l[0][0] * 100} y1={l[0][1] * 100} x2={l[1][0] * 100} y2={l[1][1] * 100}
                      stroke={c} strokeWidth={0.8} strokeDasharray="3 2" vectorEffect="non-scaling-stroke" />
                  ))}
                  {(Array.isArray(layer?.points) ? layer.points : []).map((p, i) => (
                    <g key={`clp${i}`}>
                      <circle cx={fx(p[0], p[1]) * 100} cy={fy(p[1]) * 100} r={1.6} fill={c} />
                      {typeof p[2] === "string" && p[2] ? (
                        <text x={fx(p[0], p[1]) * 100 + 1.6} y={fy(p[1]) * 100 - 1.6}
                          fontSize={3.2} fontWeight="700" fill={c}>{p[2]}</text>
                      ) : null}
                    </g>
                  ))}
                </g>
              );
            })}
          </g>
        )}

        {/* placed / reveal polygon + vertices */}
        {(kind === "points" || kind === "drag-shape" || isPoly || isTransform) && (() => {
          const showPts = revealPts ? revealPts : (kind === "drag-shape" ? placedShape : placedPts);
          const stroke = revealPts ? REVEAL_STROKE : ACTIVE_STROKE;
          // A completed closed shape (all required corners placed) fills solidly so it reads as a drawn
          // bar/shape; while still in progress it stays lighter to signal it isn't finished yet.
          const complete = pointCount > 0 && showPts.length >= pointCount;
          const fillPoly = revealPts ? REVEAL_FILL : (complete ? "rgba(25,118,210,0.42)" : "rgba(25,118,210,0.18)");
          const asPolygon = showPts.length >= 3 && (isPoly || closed);
          // Manual-fill kinds (points/polygon/reflect/rotate) while the student is answering: nothing is
          // drawn as points are placed — the outline appears only when the student taps "Connect corners"
          // and the colour fill only when they tap "Fill in", so there's never a half-drawn bowtie and no
          // wrong auto-fill. For reflect/rotate the boundary is joined in the derived reflected/rotated
          // order; for points/polygon it follows the tap order the student traced.
          // While the student answers, the outline/fill follow the Connect/Fill toggles. On a readOnly
          // review of a submitted answer (no reveal overlay) always show the finished connected+filled
          // shape so the marker sees what the student drew, not a bare set of dots.
          const manualAnswering = isManualFill && !revealPts;
          // On edge-drawing shapes the student draws the outline by hand (manual-edge branch above), so
          // the auto-connect outline is suppressed while answering — only the manual edges show. The
          // reveal/readOnly review path still auto-joins so the marker sees a finished shape.
          const showOutline = manualAnswering && !edgesAvailable && (readOnly || showConnect);
          const showFill = manualAnswering && !edgesAvailable && (readOnly || manualFill) && showPts.length >= 3;
          const orderCCW = (g) => {
            if (g.length < 3) return g;
            const cx = g.reduce((s, p) => s + p[0], 0) / g.length;
            const cy = g.reduce((s, p) => s + p[1], 0) / g.length;
            return [...g].sort((a, b) => Math.atan2(a[1] - cy, a[0] - cx) - Math.atan2(b[1] - cy, b[0] - cx));
          };
          // A CLOSED single shape drawn from bare corners (no hand-drawn edges) must be joined round its
          // boundary, not in tap order — otherwise the same four correct corners render as a self-crossing
          // bowtie purely because of the order they were tapped in, while the reveal (stored order) draws
          // the real quadrilateral. That mismatch is what makes a right answer LOOK wrong on review.
          // Grading is unaffected (it is order-independent for these); this is presentation only.
          const orderedShow = isTransform
            ? orderTransformPts(showPts)
            : (closed && shapeCount === 1 && showPts.length >= 3 && !manualEdges.length
                ? orderCCW(showPts)
                : showPts);
          // Multi-shape polygon ("sketch two rectangles"): break the placed corners into consecutive
          // runs of cornersPerShape and draw/fill each as its own quad, so two rectangles never join
          // into one bowtie. Each fully-placed run is ordered CCW round its centroid to fill cleanly.
          const shapeGroups = shapeCount > 1 && cornersPerShape > 0
            ? Array.from({ length: Math.ceil(orderedShow.length / cornersPerShape) }, (_, i) =>
                orderedShow.slice(i * cornersPerShape, i * cornersPerShape + cornersPerShape))
            : [orderedShow];
          // Manual edges (student-drawn): resolve each undirected corner-pair key back to the two placed
          // points and draw them as hand-placed segments. When they form a single closed loop, fill it
          // in the loop order the student traced (concave-safe — no centroid sort).
          // Given (context) corners are joinable on an edge-drawing question, so they must resolve here
          // too — otherwise every edge touching A/B/C fails the byCorner lookup and is silently dropped,
          // and the outline can never be drawn. The review page replays the student's submitted drawing
          // through revealValue ({points, edges}), so those edges resolve here as well — without this the
          // marker sees only dots and the drawing looks lost.
          const edgesToDraw = revealEdges ?? (manualAnswering ? manualEdges : []);
          const byCorner = new Map(
            [...showPts, ...(edgesAvailable || revealEdges ? contextPoints : [])]
              .map((p) => [cornerKey(p), p])
          );
          const manualSegs = edgesToDraw.length
            ? edgesToDraw.map((k) => k.split("|")).map(([a, b]) => [byCorner.get(a), byCorner.get(b)]).filter(([a, b]) => a && b)
            : [];
          const manualLoop = (() => {
            if (manualSegs.length < 3) return null;
            const adj = new Map();
            edgesToDraw.forEach((k) => {
              const [a, b] = k.split("|");
              if (!byCorner.get(a) || !byCorner.get(b)) return;
              adj.set(a, [...(adj.get(a) || []), b]);
              adj.set(b, [...(adj.get(b) || []), a]);
            });
            const keys = [...adj.keys()];
            if (keys.length !== manualSegs.length) return null; // every vertex must have exactly 2 edges
            if (![...adj.values()].every((n) => n.length === 2)) return null;
            const loop = []; let prev = null, cur = keys[0];
            for (let i = 0; i < keys.length; i++) {
              loop.push(byCorner.get(cur));
              const [n1, n2] = adj.get(cur);
              const nxt = n1 === prev ? n2 : n1;
              prev = cur; cur = nxt;
            }
            return cur === keys[0] ? loop : null; // closed back to start
          })();
          if (edgesToDraw.length) {
            // Fill: prefer the exact closed loop the student traced (concave-safe). If they pressed
            // "Fill in" before the loop is perfectly closed (a missing/extra edge), still colour the
            // shape using the derived transform order so the button always gives feedback rather than
            // silently doing nothing — the loop version supersedes it the moment the outline closes.
            // Gated on `closed`: an open spec (disjoint lines, e.g. "join K to L, then reflect that line")
            // has no enclosed area at all, so falling back to orderedShow here would shade a triangle
            // between unrelated line endpoints that were never meant to bound a region.
            const fillPts = closed ? (manualLoop || (showPts.length >= 3 ? orderedShow : null)) : null;
            return (
              <>
                {fillPts && (readOnly || manualFill) && (
                  <polygon points={pts(fillPts)} fill="rgba(25,118,210,0.42)" stroke="none" vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
                )}
                {manualSegs.map(([a, b], i) => (
                  <line key={`me${i}`} x1={fx(a[0], a[1]) * 100} y1={fy(a[1]) * 100} x2={fx(b[0], b[1]) * 100} y2={fy(b[1]) * 100}
                    stroke={stroke} strokeWidth={1} vectorEffect="non-scaling-stroke" strokeLinecap="round" style={{ pointerEvents: "none" }} />
                ))}
                {/* Tap an edge to remove it — the wide invisible hit line is inset from both endpoints
                    so it never covers the corners (a corner tap must arm/join, not delete the edge). */}
                {!readOnly && manualEdges.map((k, i) => {
                  const [ka, kb] = k.split("|");
                  const a = byCorner.get(ka), b = byCorner.get(kb);
                  if (!a || !b) return null;
                  const ax = fx(a[0], a[1]) * 100, ay = fy(a[1]) * 100;
                  const bx = fx(b[0], b[1]) * 100, by = fy(b[1]) * 100;
                  const t0 = 0.22, t1 = 0.78;
                  return (
                    <line key={`meh${i}`}
                      x1={ax + (bx - ax) * t0} y1={ay + (by - ay) * t0}
                      x2={ax + (bx - ax) * t1} y2={ay + (by - ay) * t1}
                      stroke="rgba(0,0,0,0)" strokeWidth={3} vectorEffect="non-scaling-stroke"
                      style={{ cursor: "pointer", pointerEvents: "stroke" }}
                      onClick={(e) => { e.stopPropagation(); setManualEdges((es) => es.filter((x) => x !== k)); }} />
                  );
                })}
                {showPts.map((pt, i) => {
                  const [x, y] = pt;
                  const isPend = edgesAvailable && edgePending === i;
                  const typed = (typeof pt[2] === "string" && pt[2] !== "") ? pt[2] : "";
                  const lbl = typed || ((spec?.require_point_labels && !revealPts) ? "" : pointLabels[i]);
                  return (
                    <g key={`p${i}`}>
                      {isPend && <circle cx={fx(x, y) * 100} cy={fy(y) * 100} r={3.4} fill="none" stroke="#0d47a1" strokeWidth={1} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />}
                      <circle cx={fx(x, y) * 100} cy={fy(y) * 100} r={isPend ? 2.6 : 2} fill={isPend ? "#0d47a1" : stroke} style={{ pointerEvents: "none" }} />
                      {/* Delete × shows ONLY on the armed corner, so it never clutters the grid and can't
                          collide with the tap rhythm of working round the shape (tap a corner to end an
                          edge, tap it again to start the next). Tap the corner to arm it → its × appears. */}
                      {isPend && !readOnly && (
                        <g style={{ cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); removeCorner(i); }}>
                          <circle cx={fx(x, y) * 100 + 3.4} cy={fy(y) * 100 - 3.4} r={2} fill="#fff" stroke="#c62828" strokeWidth={0.5} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "all" }} />
                          <text x={fx(x, y) * 100 + 3.4} y={fy(y) * 100 - 3.4} fontSize={3} fontWeight={700} fill="#c62828" textAnchor="middle" dominantBaseline="central" style={{ pointerEvents: "none", userSelect: "none" }}>×</text>
                        </g>
                      )}
                      {lbl && !labelOverlayActive ? <text x={fx(x, y) * 100 + 1.6} y={fy(y) * 100 - 1.6} fontSize={3.4} fontWeight="700" fill={stroke} style={{ pointerEvents: "none", userSelect: "none" }}>{lbl}</text> : null}
                    </g>
                  );
                })}
              </>
            );
          }
          return (
            <>
              {manualAnswering
                ? ((showOutline || showFill) && showPts.length >= 2 && shapeGroups.map((g, gi) => (
                    g.length >= 2 ? (
                      <polygon key={`sg${gi}`} points={pts(orderCCW(g))}
                        fill={showFill && g.length >= 3 ? "rgba(25,118,210,0.42)" : "none"}
                        stroke={showOutline ? stroke : "none"} strokeWidth={0.6}
                        vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
                    ) : null
                  )))
                : (connect && showPts.length >= 2 && (
                    shapeCount > 1
                      ? shapeGroups.map((g, gi) => (
                          g.length >= 2 ? (
                            g.length >= 3 && closed
                              ? <polygon key={`sg${gi}`} points={pts(orderCCW(g))} fill={fillPoly} stroke={stroke} strokeWidth={0.6} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
                              : <polyline key={`sg${gi}`} points={pts(g)} fill="none" stroke={stroke} strokeWidth={0.6} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
                          ) : null
                        ))
                      : asPolygon
                        ? <polygon points={pts(showPts)} fill={fillPoly} stroke={stroke} strokeWidth={0.6} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
                        : <polyline points={pts(showPts)} fill="none" stroke={stroke} strokeWidth={0.6} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
                  ))}
              {showPts.map((pt, i) => {
                const [x, y] = pt;
                // Prefer a label the student typed on the point itself (3rd element), then fall back
                // to the admin's positional point_labels guidance. But when the label is REQUIRED and
                // graded (require_point_labels) and we're not showing the reveal, never draw the
                // expected letter as guidance — that would hand the student the answer and look like it
                // auto-filled. Only their own typed label shows; the empty input box prompts them.
                const typed = (typeof pt[2] === "string" && pt[2] !== "") ? pt[2] : "";
                const lbl = typed || ((spec?.require_point_labels && !revealPts) ? "" : pointLabels[i]);
                const isPend = edgesAvailable && edgePending === i;
                return (
                  <g key={`p${i}`}>
                    {isPend && <circle cx={fx(x, y) * 100} cy={fy(y) * 100} r={3.4} fill="none" stroke="#0d47a1" strokeWidth={1} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />}
                    <circle
                      cx={fx(x, y) * 100}
                      cy={fy(y) * 100}
                      r={isPend ? 2.6 : 2}
                      fill={isPend ? "#0d47a1" : stroke}
                      style={{ cursor: kind === "drag-shape" && !readOnly ? "grab" : "default", pointerEvents: kind === "drag-shape" && !readOnly ? "all" : "none" }}
                      onPointerDown={kind === "drag-shape" ? onShapePointerDown : undefined}
                    />
                    {/* Delete × on the armed corner only (see manual-edge branch) — tap a corner to arm
                        it, its × appears; no always-on badge, no collision with the edge-drawing rhythm. */}
                    {isPend && !readOnly && (
                      <g style={{ cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); removeCorner(i); }}>
                        <circle cx={fx(x, y) * 100 + 3.4} cy={fy(y) * 100 - 3.4} r={2} fill="#fff" stroke="#c62828" strokeWidth={0.5} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "all" }} />
                        <text x={fx(x, y) * 100 + 3.4} y={fy(y) * 100 - 3.4} fontSize={3} fontWeight={700} fill="#c62828" textAnchor="middle" dominantBaseline="central" style={{ pointerEvents: "none", userSelect: "none" }}>×</text>
                      </g>
                    )}
                    {lbl && !labelOverlayActive ? (
                      <text x={fx(x, y) * 100 + 1.6} y={fy(y) * 100 - 1.6} fontSize={3.4} fontWeight="700" fill={stroke} style={{ pointerEvents: "none", userSelect: "none" }}>
                        {lbl}
                      </text>
                    ) : null}
                  </g>
                );
              })}
            </>
          );
        })()}

        {/* LINES — tap two points (no grid) to draw each line; repeat for several. The capture rect
            sits first so taps start a new line; per-line hit lines paint on top to allow removal. */}
        {isLines && (() => {
          const showLines = revealLines ? revealLines : placedLines;
          const stroke = revealLines ? REVEAL_STROKE : ACTIVE_STROKE;
          // "Mark the parallel lines with an arrow" — the standard maths notation is a chevron drawn ON the
          // side, not a plain segment. A `lines` spec can opt into it (line_style: "arrow"); the geometry,
          // tapping and grading are identical, only the glyph differs.
          const arrowLines = spec?.line_style === "arrow";
          const chevron = (l, i, colour) => {
            const [[x1, y1], [x2, y2]] = l;
            const mx = (x1 + x2) / 2 * 100;
            const my = (y1 + y2) / 2 * 100;
            const ang = Math.atan2(y2 - y1, x2 - x1);
            const size = 2.6;
            // Two strokes swept back from the midpoint, forming a ">" pointing along the line.
            const p = (spread) => [
              mx - size * Math.cos(ang - spread),
              my - size * Math.sin(ang - spread),
            ];
            const [ax, ay] = p(0.55);
            const [bx, by] = p(-0.55);
            return (
              <polyline key={`ar${i}`} points={`${ax},${ay} ${mx},${my} ${bx},${by}`}
                fill="none" stroke={colour} strokeWidth={1.1} strokeLinecap="round" strokeLinejoin="round"
                vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
            );
          };
          return (
            <>
              {!readOnly && (
                <rect x={0} y={0} width={100} height={100} fill="rgba(0,0,0,0)"
                  style={{ cursor: "crosshair", pointerEvents: "all" }}
                  onClick={(e) => tapLine(e.clientX, e.clientY)} />
              )}
              {showLines.map((l, i) => (
                <line key={`ln${i}`} x1={l[0][0] * 100} y1={l[0][1] * 100} x2={l[1][0] * 100} y2={l[1][1] * 100}
                  stroke={stroke} strokeWidth={0.8} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
              ))}
              {arrowLines && showLines.map((l, i) => chevron(l, i, stroke))}
              {!readOnly && placedLines.map((l, i) => (
                <line key={`lh${i}`} x1={l[0][0] * 100} y1={l[0][1] * 100} x2={l[1][0] * 100} y2={l[1][1] * 100}
                  stroke="rgba(0,0,0,0)" strokeWidth={3} vectorEffect="non-scaling-stroke"
                  style={{ cursor: "pointer", pointerEvents: "stroke" }} onClick={(e) => removeLine(i, e)} />
              ))}
              {showLines.flatMap((l, i) => l.slice(0, 2).map(([x, y], k) => (
                <circle key={`le${i}-${k}`} cx={x * 100} cy={y * 100} r={1.4} fill={stroke} style={{ pointerEvents: "none" }} />
              )))}
              {!readOnly && pendingPt && (
                <circle cx={pendingPt[0] * 100} cy={pendingPt[1] * 100} r={1.8} fill="rgba(25,118,210,0.6)" style={{ pointerEvents: "none" }} />
              )}
            </>
          );
        })()}

        {/* CUT-DRAG — tap two points to cut the shape; the pieces become draggable so the student can
            slide one to reassemble the target shape. Reveal shows the canonical cut + assembled pieces. */}
        {isCutDrag && (() => {
          const fills = cdReveal ? [REVEAL_FILL, REVEAL_FILL] : ["rgba(25,118,210,0.30)", "rgba(237,108,2,0.30)"];
          const strokes = cdReveal ? [REVEAL_STROKE, REVEAL_STROKE] : ["#1976d2", "#ed6c02"];
          return (
            <>
              {!readOnly && !cdValue && !cdReveal && (
                <rect x={0} y={0} width={100} height={100} fill="rgba(0,0,0,0)"
                  style={{ cursor: "crosshair", pointerEvents: "all" }}
                  onClick={(e) => tapCut(e.clientX, e.clientY)} />
              )}
              {!readOnly && !cdValue && pendingPt && (
                <circle cx={pendingPt[0] * 100} cy={pendingPt[1] * 100} r={1.8} fill="rgba(25,118,210,0.6)" style={{ pointerEvents: "none" }} />
              )}
              {/* Cut drawn but missing the shape — show it so the student can remove and retry. */}
              {cdShow && !cdPieces && (
                <line x1={cdShow.cut[0][0] * 100} y1={cdShow.cut[0][1] * 100} x2={cdShow.cut[1][0] * 100} y2={cdShow.cut[1][1] * 100}
                  stroke={cdReveal ? REVEAL_STROKE : ACTIVE_STROKE} strokeWidth={0.9} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
              )}
              {/* Where the cut was made, dashed, under the (possibly moved) pieces. */}
              {cdPieces && (
                <line x1={cdShow.cut[0][0] * 100} y1={cdShow.cut[0][1] * 100} x2={cdShow.cut[1][0] * 100} y2={cdShow.cut[1][1] * 100}
                  stroke={cdReveal ? REVEAL_STROKE : "#888"} strokeWidth={0.6} strokeDasharray="2 2" vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
              )}
              {cdPieces && cdPieces.map((poly, i) => {
                const ox = Number(cdOffsets[i]?.[0]) || 0, oy = Number(cdOffsets[i]?.[1]) || 0;
                return (
                  <polygon key={`cd${i}`}
                    points={poly.map(([x, y]) => `${(x + ox) * 100},${(y + oy) * 100}`).join(" ")}
                    fill={fills[i]} stroke={strokes[i]} strokeWidth={0.7} vectorEffect="non-scaling-stroke"
                    style={{ cursor: readOnly ? "default" : "grab", pointerEvents: readOnly ? "none" : "all", touchAction: "none" }}
                    onPointerDown={readOnly ? undefined : onPiecePointerDown(i)} />
                );
              })}
            </>
          );
        })()}

        {/* SEGMENTS — join two of the fixed nodes (crosses) with a straight line. Tap a node to start,
            tap another to draw; tap a drawn line to remove it. */}
        {isSegments && (() => {
          const showSegs = revealSegs ? revealSegs : placedSegs;
          const stroke = revealSegs ? REVEAL_STROKE : ACTIVE_STROKE;
          return (
            <>
              {showSegs.map((l, i) => (
                <line key={`sg${i}`} x1={l[0][0] * 100} y1={l[0][1] * 100} x2={l[1][0] * 100} y2={l[1][1] * 100}
                  stroke={stroke} strokeWidth={0.8} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
              ))}
              {!readOnly && placedSegs.map((l, i) => (
                <line key={`sh${i}`} x1={l[0][0] * 100} y1={l[0][1] * 100} x2={l[1][0] * 100} y2={l[1][1] * 100}
                  stroke="rgba(0,0,0,0)" strokeWidth={3} vectorEffect="non-scaling-stroke"
                  style={{ cursor: "pointer", pointerEvents: "stroke" }} onClick={(e) => removeSeg(i, e)} />
              ))}
              {nodes.map((n, i) => {
                const cx = Number(n[0]) * 100, cy = Number(n[1]) * 100, on = pendingNode === i;
                return (
                  <g key={`nd${i}`}>
                    <line x1={cx - 1.2} y1={cy - 1.2} x2={cx + 1.2} y2={cy + 1.2} stroke="#111" strokeWidth={0.7} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
                    <line x1={cx - 1.2} y1={cy + 1.2} x2={cx + 1.2} y2={cy - 1.2} stroke="#111" strokeWidth={0.7} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
                    {on && <circle cx={cx} cy={cy} r={2.4} fill="none" stroke={ACTIVE_STROKE} strokeWidth={0.8} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />}
                    {!readOnly && (
                      <circle cx={cx} cy={cy} r={3} fill="rgba(0,0,0,0)" style={{ cursor: "pointer", pointerEvents: "all" }} onClick={() => tapNode(i)} />
                    )}
                  </g>
                );
              })}
            </>
          );
        })()}

        {/* GLYPH tap-capture — a tap on empty space adds a new glyph. The letters themselves render as
            HTML overlays (below) so they keep correct proportions despite the non-uniform viewBox. */}
        {isGlyph && !readOnly && (
          <rect x={0} y={0} width={100} height={100} fill="rgba(0,0,0,0)"
            style={{ cursor: "text", pointerEvents: "all" }}
            // Clicking empty canvas while a letter is selected DESELECTS it (closing the toolbar) rather
            // than dropping another letter — otherwise there is no way to dismiss the toolbar without
            // adding an unwanted glyph. Click again on empty canvas to add the next one.
            onClick={(e) => {
              if (activeGlyph != null) {
                setSelGlyph(null);
                return;
              }
              addGlyph(e.clientX, e.clientY);
            }} />
        )}

        {/* MARKER — tap along an axis (number line / scale / bar chart) to mark a value. The capture
            rect projects the tap onto the axis; the placed mark renders as an arrow or a filled bar. */}
        {isMarker && markerAxis && (() => {
          const a = markerAxis[0], b = markerAxis[1];
          const show = revealMarkerPt || markerPt;
          const stroke = revealMarkerPt ? REVEAL_STROKE : ACTIVE_STROKE;
          const dx = (b[0] - a[0]) * 100, dy = (b[1] - a[1]) * 100;
          const dl = Math.hypot(dx, dy) || 1;
          const px = -dy / dl, py = dx / dl; // unit perpendicular in viewBox units
          const wU = markerBarWidth * 100;
          return (
            <>
              {!readOnly && (
                <rect x={0} y={0} width={100} height={100} fill="rgba(0,0,0,0)"
                  style={{ cursor: "pointer", pointerEvents: "all" }} onClick={(e) => tapMarker(e.clientX, e.clientY)} />
              )}
              <line x1={a[0] * 100} y1={a[1] * 100} x2={b[0] * 100} y2={b[1] * 100}
                stroke="rgba(25,118,210,0.25)" strokeWidth={0.5} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
              {show && markerStyle === "bar" && (
                <polygon
                  points={[[a[0] * 100 + px * wU, a[1] * 100 + py * wU], [show[0] * 100 + px * wU, show[1] * 100 + py * wU], [show[0] * 100 - px * wU, show[1] * 100 - py * wU], [a[0] * 100 - px * wU, a[1] * 100 - py * wU]].map((p) => p.join(",")).join(" ")}
                  fill={revealMarkerPt ? REVEAL_FILL : "rgba(25,118,210,0.35)"} stroke={stroke} strokeWidth={0.5} vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
              )}
              {show && markerStyle !== "bar" && (
                <g style={{ pointerEvents: "none" }}>
                  <line x1={show[0] * 100 - px * 4} y1={show[1] * 100 - py * 4} x2={show[0] * 100 + px * 4} y2={show[1] * 100 + py * 4}
                    stroke={stroke} strokeWidth={0.9} vectorEffect="non-scaling-stroke" />
                  <circle cx={show[0] * 100} cy={show[1] * 100} r={1.6} fill={stroke} />
                </g>
              )}
            </>
          );
        })()}

        {/* CLOCK — drag the hour (short) and minute (long) hands about the clock centre. */}
        {isClock && (() => {
          const hands = revealHands || clockHands || (readOnly ? null : defaultHands);
          if (!hands) return null;
          const stroke = revealHands ? REVEAL_STROKE : ACTIVE_STROKE;
          const cx = clockCenter[0] * 100, cy = clockCenter[1] * 100;
          return (
            <g>
              <circle cx={cx} cy={cy} r={1.4} fill={stroke} style={{ pointerEvents: "none" }} />
              {[0, 1].map((idx) => {
                const h = hands[idx];
                if (!Array.isArray(h)) return null;
                const which = idx === 0 ? "hour" : "minute";
                return (
                  <g key={which}>
                    <line x1={cx} y1={cy} x2={h[0] * 100} y2={h[1] * 100}
                      stroke={stroke} strokeWidth={idx === 0 ? 1.4 : 0.9} strokeLinecap="round" vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
                    {!readOnly && !revealHands && (
                      <circle cx={h[0] * 100} cy={h[1] * 100} r={2.6} fill={idx === 0 ? "rgba(25,118,210,0.85)" : "rgba(25,118,210,0.55)"}
                        style={{ cursor: "grab", pointerEvents: "all" }} onPointerDown={startHand(which)} />
                    )}
                  </g>
                );
              })}
            </g>
          );
        })()}

        {/* whole-grid drag layer (admin authoring) — sits on top so a drag moves the entire grid
            instead of toggling/placing; toggle off to tap individual cells/points again. */}
        {gridMovable && onMoveGrid && (
          <rect
            x={-50} y={-50} width={200} height={200}
            fill="rgba(25,118,210,0.05)"
            style={{ cursor: gridDrag ? "grabbing" : "grab", pointerEvents: "all", touchAction: "none" }}
            onPointerDown={onGridDragStart}
          />
        )}
      </svg>

      {/* Marker label — "Mark with letter A the likelihood that…". The letter is fixed by the question (it
          is never typed, so nothing extra is graded), and it is drawn just above the mark.
          Rendered as an HTML overlay, NOT SVG <text>: the overlay viewBox is 0..100 in both axes with
          preserveAspectRatio="none", so on a wide figure (this scale is 587x107, ~5.5:1) any SVG text is
          stretched 5.5x horizontally into an unreadable smear. The pictogram icons dodge the same trap the
          same way. */}
      {/* Nothing at all is drawn on the scale until the student taps, so there is no cue as to WHICH letter
          they are placing. Show it up-front as a chip in the corner ("Place: C"), which then disappears
          once the mark is down and the letter is drawn on the scale itself. */}
      {isMarker && markerAxis && spec?.marker_label && !readOnly && !markerPt && !revealMarkerPt && (
        <Box
          sx={{
            position: "absolute",
            left: 6,
            top: 4,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            px: 0.75,
            py: 0.25,
            borderRadius: 1,
            bgcolor: "rgba(255,255,255,0.92)",
            border: "1px solid",
            borderColor: ACTIVE_STROKE,
            fontSize: 11,
            fontWeight: 600,
            color: ACTIVE_STROKE,
            pointerEvents: "none",
            userSelect: "none",
          }}
        >
          Place:
          <Box component="span" sx={{ fontSize: 16, fontWeight: 800, lineHeight: 1 }}>
            {String(spec.marker_label)}
          </Box>
        </Box>
      )}

      {isMarker && markerAxis && spec?.marker_label && (() => {
        const show = revealMarkerPt || markerPt;
        if (!show) return null;
        const colour = revealMarkerPt ? REVEAL_STROKE : ACTIVE_STROKE;
        return (
          <Box
            sx={{
              position: "absolute",
              left: `${show[0] * 100}%`,
              top: `${show[1] * 100}%`,
              transform: "translate(-50%, -175%)", // sit clear above the tick
              fontSize: 18,
              fontWeight: 800,
              lineHeight: 1,
              color: colour,
              // The letter is an annotation on the printed scale (whose own captions are ~11-12px), so it
              // needs to read clearly as the student's mark rather than blend into the artwork.
              textShadow: "0 0 3px #fff, 0 0 3px #fff",
              pointerEvents: "none",
              userSelect: "none",
            }}
          >
            {String(spec.marker_label)}
          </Box>
        );
      })()}

      {/* Point labels (A/B/C …) as crisp HTML overlays for the reveal + read-only review, so the letters
          stay legible on a wide figure where SVG <text> would be smeared flat by the non-uniform viewBox
          (same reason the marker letter and pictogram icons are HTML). The SVG <text> path above is
          skipped while this is active. The live-answering student keeps the inline input box / chip. */}
      {labelOverlayActive && (kind === "points" || kind === "drag-shape" || isPoly || isTransform) && (() => {
        const labelPts = revealPts ? revealPts : placedPts;
        const colour = revealPts ? REVEAL_STROKE : ACTIVE_STROKE;
        return labelPts.map((pt, i) => {
          const typed = (typeof pt[2] === "string" && pt[2] !== "") ? pt[2] : "";
          const lbl = typed || ((spec?.require_point_labels && !revealPts) ? "" : pointLabels[i]);
          if (!lbl) return null;
          const [x, y] = pt;
          return (
            <Box
              key={`plbl${i}`}
              sx={{
                position: "absolute",
                left: `${fx(x, y) * 100}%`,
                top: `${fy(y) * 100}%`,
                transform: "translate(-50%, -155%)", // centred just above the point/tick
                fontSize: 16,
                fontWeight: 800,
                lineHeight: 1,
                color: colour,
                textShadow: "0 0 3px #fff, 0 0 3px #fff",
                pointerEvents: "none",
                userSelect: "none",
              }}
            >
              {lbl}
            </Box>
          );
        });
      })()}

      {/* Clear-last control for the lines tool — removes the in-progress endpoint, then the last line. */}
      {isLines && !readOnly && onChange && (placedLines.length > 0 || pendingPt) && (
        <Box
          component="button"
          type="button"
          onClick={clearLastLine}
          sx={{
            position: "absolute", top: 4, right: 4, zIndex: 5,
            px: 0.75, py: 0.25, fontSize: "0.7rem", fontWeight: 700,
            color: ACTIVE_STROKE, border: "1px solid", borderColor: ACTIVE_STROKE,
            borderRadius: 0.5, bgcolor: "rgba(255,255,255,0.9)", cursor: "pointer",
          }}
        >
          {pendingPt ? "Cancel point" : "Clear last line"}
        </Box>
      )}

      {/* Clear-last control for the segments tool — cancels the pending node, then removes the last join. */}
      {isSegments && !readOnly && onChange && (placedSegs.length > 0 || pendingNode !== null) && (
        <Box
          component="button"
          type="button"
          onClick={clearLastSeg}
          sx={{
            position: "absolute", top: 4, right: 4, zIndex: 5,
            px: 0.75, py: 0.25, fontSize: "0.7rem", fontWeight: 700,
            color: ACTIVE_STROKE, border: "1px solid", borderColor: ACTIVE_STROKE,
            borderRadius: 0.5, bgcolor: "rgba(255,255,255,0.9)", cursor: "pointer",
          }}
        >
          {pendingNode !== null ? "Cancel" : "Clear last line"}
        </Box>
      )}

      {/* Required-label points: the corner tap now arms an edge (so the shape can still be drawn), so
          re-labelling needs its own target. This chip shows the typed letter — or "label" when the point
          is still unnamed — and re-opens the modal prompt on click. */}
      {labelRequired && !readOnly && onChange && placedPts.map((pt, i) => {
        const [x, y] = pt;
        const typed = typeof pt[2] === "string" ? pt[2].trim() : "";
        return (
          <Box
            key={`rlbl${i}`}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              setLabelPrompt({ index: i, value: typed, isNew: false });
            }}
            sx={{
              position: "absolute",
              left: `${fx(x, y) * 100}%`,
              top: `${fy(y) * 100}%`,
              transform: "translate(8px, -50%)",
              px: 0.6,
              py: 0.125,
              fontSize: "0.75rem",
              fontWeight: 700,
              lineHeight: 1.5,
              cursor: "pointer",
              color: typed ? ACTIVE_STROKE : "#b45309",
              border: "1px dashed",
              borderColor: typed ? ACTIVE_STROKE : "#f59e0b",
              borderRadius: 0.5,
              bgcolor: "rgba(255,255,255,0.92)",
              zIndex: 5,
            }}
          >
            {typed || "label"}
          </Box>
        );
      })}

      {/* Whole-shape label ("Label the triangle A"): shown at the centroid. While answering, a chip
          re-opens the forced popup; on reveal/review the letter is painted as a crisp HTML overlay. */}
      {(() => {
        const displayLabel = revealShapeLabel || currentShapeLabel;
        // Edges-only questions ("join them in order to form a triangle. Label the triangle A"): every
        // corner is already printed, so no_new_points keeps placedPts empty and the centroid had no
        // points to sit on — the label chip never rendered, leaving no way to open the popup and no
        // place to paint a typed or revealed letter. The printed corners ARE the shape here.
        // given_points before contextPoints: on a shared grid contextPoints also carries the marks other
        // parts made (part e's translated triangle), which would drag the centroid off this shape.
        const printedCorners = Array.isArray(spec?.given_points) && spec.given_points.length >= 3
          ? spec.given_points
          : contextPoints;
        const shapeCorners = placedPts.length >= 3
          ? placedPts
          : (spec?.no_new_points ? printedCorners : placedPts);
        const ptsForCentroid = revealPts && revealPts.length >= 3 ? revealPts : shapeCorners;
        if (ptsForCentroid.length < 3) return null;
        if (!displayLabel && !(requireShapeLabel && !readOnly && onChange)) return null;
        const cx = ptsForCentroid.reduce((s, p) => s + Number(p[0]), 0) / ptsForCentroid.length;
        const cy = ptsForCentroid.reduce((s, p) => s + Number(p[1]), 0) / ptsForCentroid.length;
        if (labelOverlayActive && displayLabel) {
          return (
            <Box
              key="shape-lbl-reveal"
              sx={{
                position: "absolute",
                left: `${fx(cx, cy) * 100}%`,
                top: `${fy(cy) * 100}%`,
                transform: "translate(-50%, -50%)",
                fontSize: 20,
                fontWeight: 800,
                lineHeight: 1,
                color: revealPts ? REVEAL_STROKE : ACTIVE_STROKE,
                textShadow: "0 0 3px #fff, 0 0 3px #fff",
                pointerEvents: "none",
                userSelect: "none",
                zIndex: 6,
              }}
            >
              {displayLabel}
            </Box>
          );
        }
        if (!requireShapeLabel || readOnly || !onChange) return null;
        return (
          <Box
            key="shape-lbl-chip"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              setShapeLabelPrompt({ value: currentShapeLabel });
            }}
            sx={{
              position: "absolute",
              left: `${fx(cx, cy) * 100}%`,
              top: `${fy(cy) * 100}%`,
              transform: "translate(-50%, -50%)",
              px: 0.75,
              py: 0.25,
              fontSize: "0.85rem",
              fontWeight: 800,
              lineHeight: 1.4,
              cursor: "pointer",
              color: currentShapeLabel ? ACTIVE_STROKE : "#b45309",
              border: "1px dashed",
              borderColor: currentShapeLabel ? ACTIVE_STROKE : "#f59e0b",
              borderRadius: 0.5,
              bgcolor: "rgba(255,255,255,0.94)",
              zIndex: 6,
            }}
          >
            {currentShapeLabel || "label shape"}
          </Box>
        );
      })()}

      {/* Editable per-point labels — student (or admin) types a label next to each plotted point.
          Stored as the 3rd element of the point so deterministic coordinate grading is unaffected.
          When the label is REQUIRED the modal prompt (below) is the entry path instead of this inline
          box, so the student can't overlook it. */}
      {allowLabels && !labelRequired && (kind === "points" || kind === "drag-shape" || isTransform || isPoly) && !readOnly && onChange && placedPts.map((pt, i) => {
        const [x, y] = pt;
        return (
          <Box
            key={`lbl${i}`}
            component="input"
            value={typeof pt[2] === "string" ? pt[2] : ""}
            placeholder="label"
            onChange={(e) => setPointLabel(i, e.target.value)}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: "absolute",
              left: `${fx(x, y) * 100}%`,
              top: `${fy(y) * 100}%`,
              transform: "translate(8px, -50%)",
              width: 52,
              px: 0.5,
              py: 0.125,
              fontSize: "0.75rem",
              fontWeight: 700,
              color: ACTIVE_STROKE,
              border: "1px solid",
              borderColor: ACTIVE_STROKE,
              borderRadius: 0.5,
              bgcolor: "rgba(255,255,255,0.9)",
              outline: "none",
              zIndex: 5,
            }}
          />
        );
      })}

      {/* Multi-group sector captions — each category's name is painted once, at the centre of the run
          of regions the student coloured for it, so the finished chart reads like the worked "sleeping"
          example (a coloured sector with its activity named on it). Purely derived from the assignment:
          colouring the sector labels it, so there is nothing extra to type. */}
      {isGroupShade && shadeGroups.map((g) => {
        const key = String(g.key);
        const src = revealIds ? revealGroupOfRegion : groupOfRegion;
        const mine = regions.filter((rg) => src[String(rg.id)] === key);
        if (mine.length === 0) return null;
        const cs = mine.map(regionCentroid);
        const cx = cs.reduce((s, p) => s + p[0], 0) / cs.length;
        const cy = cs.reduce((s, p) => s + p[1], 0) / cs.length;
        const c = groupColorByKey[key];
        return (
          <Box
            key={`grp${key}`}
            sx={{
              position: "absolute",
              left: `${cx * 100}%`,
              top: `${cy * 100}%`,
              transform: "translate(-50%, -50%)",
              fontSize: "0.8rem",
              fontWeight: 700,
              color: c.stroke,
              pointerEvents: "none",
              bgcolor: "rgba(255,255,255,0.82)",
              px: 0.5,
              borderRadius: 0.5,
              whiteSpace: "nowrap",
              zIndex: 5,
            }}
          >
            {g.label || key}
          </Box>
        );
      })}

      {/* Shaded-region typed labels (require_labels / label_only shade questions, e.g. a proportional
          pie chart where each shaded sector must also be captioned). Editable while answering; static
          text while read-only (reviewing the student's own answer, or the green reveal overlay).
          `label_only` regions are `given` (pre-shaded) so their box shows unconditionally — there is
          no shading step gating it. */}
      {(requireLabels || labelOnly || letterRegionIds.size > 0) && regions.map((rg) => {
        const id = String(rg.id);
        const isLetterCell = letterRegionIds.has(id);
        const shown = isLetterCell
          || (labelOnly
            ? isLabelable(rg)
            : (requireLabels && (revealIds ? revealIds.includes(id) : shadedIds.includes(id))));
        if (!shown) return null;
        const [cx, cy] = regionCentroid(rg);
        const groupColor = labelGroupColorOf(rg);
        const style = letterRegionStyle[id];
        const boxColor = style?.color || groupColor?.stroke || (revealIds ? REVEAL_STROKE : ACTIVE_STROKE);
        const boxWidth = style?.size || 92;
        const boxFontSize = `${Math.max(0.65, boxWidth / 115)}rem`;
        // Letter-entry cells: no persistent input box (students couldn't tell which shape a floating
        // box belonged to when several sat close together). Tapping the SHAPE itself opens a forced
        // popup (the letterPrompt dialog below) — this overlay is just a tap-through hint/answer
        // display, so pointer events must stay off or it would shadow the polygon's onClick.
        if (isLetterCell) {
          const text = revealIds ? (rg.label || letterRegionChar[id] || "") : (shadeLabels[id] || "");
          const interactive = !readOnly && !!onChange;
          if (!text && !interactive) return null;
          return (
            <Box
              key={`rlbl${id}`}
              sx={{
                position: "absolute",
                left: `${cx * 100}%`,
                top: `${cy * 100}%`,
                transform: "translate(-50%, -50%)",
                minWidth: text ? undefined : boxWidth * 0.45,
                minHeight: text ? undefined : boxWidth * 0.45,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                px: 0.5,
                py: 0.25,
                fontSize: boxFontSize,
                fontWeight: 700,
                color: revealIds ? REVEAL_STROKE : boxColor,
                border: text ? "none" : "1.5px dashed",
                borderColor: boxColor,
                borderRadius: text ? 0.5 : "50%",
                bgcolor: text ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.55)",
                pointerEvents: "none",
                zIndex: 5,
              }}
            >
              {text || (interactive ? "?" : "")}
            </Box>
          );
        }
        if (!readOnly && onChange) {
          return (
            <Box
              key={`rlbl${id}`}
              component="input"
              value={shadeLabels[id] || ""}
              placeholder="drop or type label"
              onChange={(e) => setRegionLabel(id, e.target.value)}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
              onDrop={(e) => {
                e.preventDefault();
                const dragged = e.dataTransfer.getData("text/plain");
                if (dragged) setRegionLabel(id, dragged);
              }}
              sx={{
                position: "absolute",
                left: `${cx * 100}%`,
                top: `${cy * 100}%`,
                transform: "translate(-50%, -50%)",
                width: boxWidth,
                textAlign: "center",
                px: 0.5,
                py: 0.25,
                fontSize: boxFontSize,
                fontWeight: 700,
                color: boxColor,
                border: "1.5px dashed",
                borderColor: boxColor,
                borderRadius: 0.5,
                bgcolor: "rgba(255,255,255,0.92)",
                outline: "none",
                zIndex: 5,
              }}
            />
          );
        }
        const text = revealIds ? (rg.label || letterRegionChar[id] || "") : (shadeLabels[id] || "");
        if (!text) return null;
        return (
          <Box
            key={`rlbl${id}`}
            sx={{
              position: "absolute",
              left: `${cx * 100}%`,
              top: `${cy * 100}%`,
              transform: "translate(-50%, -50%)",
              fontSize: boxFontSize,
              fontWeight: 700,
              color: revealIds ? REVEAL_STROKE : boxColor,
              pointerEvents: "none",
              bgcolor: "rgba(255,255,255,0.85)",
              px: 0.5,
              borderRadius: 0.5,
              zIndex: 5,
            }}
          >
            {text}
          </Box>
        );
      })}

      {/* Pictogram icons — a row of icons (bottle / lorry / …, per spec.icon) the student fills in
          quarters. Rendered as HTML overlays (fixed aspect) so the icon isn't distorted by the
          non-uniform image viewBox. Tap an icon to cycle empty → full → ¾ → ½ → ¼ → empty. */}
      {isPictogram && pictoCount > 0 && (() => {
        const states = revealPicto || pictoStates;
        const color = revealPicto ? REVEAL_STROKE : ACTIVE_STROKE;
        const rx0 = Number(pictoRow.x0) || 0, ry = Number(pictoRow.y) || 0.5, rdx = Number(pictoRow.dx) || 0.1;
        const icon = pictoIcon(spec?.icon);
        const vbH = Number((icon.vb.split(" ")[3]) || 60);
        const wPx = Math.max(9, rdx * box.w * 0.66);
        const hPx = wPx * icon.aspect;
        return Array.from({ length: pictoCount }).map((_, i) => {
          const st = states[i] || 0;
          const partRects = pictoClipRects(st, icon.halfW, vbH / 2);
          return (
            <Box key={`pic${i}`}
              onClick={readOnly ? undefined : (e) => { e.stopPropagation(); cycleIcon(i); }}
              sx={{
                position: "absolute", left: `${(rx0 + i * rdx) * 100}%`, top: `${ry * 100}%`,
                transform: "translate(-50%, -50%)", width: wPx, height: hPx,
                cursor: readOnly ? "default" : "pointer", pointerEvents: readOnly ? "none" : "all", zIndex: 4,
              }}>
              <svg viewBox={icon.vb} width={wPx} height={hPx} style={{ display: "block" }}>
                {partRects && (
                  <defs>
                    <clipPath id={`h-${uid}-${i}`}>
                      {partRects.map(([x, y, w, h], r) => <rect key={r} x={x} y={y} width={w} height={h} />)}
                    </clipPath>
                  </defs>
                )}
                {st === 1 && <path d={icon.d} fill={color} fillOpacity="0.5" />}
                {partRects && <path d={icon.d} fill={color} fillOpacity="0.5" clipPath={`url(#h-${uid}-${i})`} />}
                <path d={icon.d} fill="none" stroke={color} strokeWidth="1.6" />
              </svg>
            </Box>
          );
        });
      })()}

      {/* Glyph letters — rendered as HTML overlays (not SVG text) so they keep correct proportions on
          non-square images. Shown for the student's answer and for the correct-answer reveal. */}
      {isGlyph && (() => {
        const show = revealGlyphs ? revealGlyphs : placedGlyphs;
        const color = revealGlyphs ? REVEAL_STROKE : ACTIVE_STROKE;
        const fontPx = grid
          ? Math.max(10, Math.min(gdx * box.w, gdy * box.h) * 0.72)
          : Math.max(12, (Number(spec?.glyph_size) || 0.3) * box.h);
        const editable = !revealGlyphs && !readOnly && onChange;
        return show.map((g, i) => {
          const left = (g.pos?.[0] ?? 0) * 100, top = (g.pos?.[1] ?? 0) * 100;
          const sx = g.flipH ? -1 : 1, sy = g.flipV ? -1 : 1;
          const isSel = editable && i === activeGlyph;
          return (
            <Box key={`glh${i}`}
              tabIndex={editable ? 0 : undefined}
              onPointerDown={editable ? (e) => dragGlyph(i, e) : undefined}
              onClick={editable ? (e) => { e.stopPropagation(); setSelGlyph(i); } : undefined}
              onKeyDown={editable ? (e) => {
                const step = e.shiftKey ? 4 : 1;
                const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
                if (!d) return;
                e.preventDefault();
                e.stopPropagation();
                nudgeGlyph(i, d[0], d[1]);
              } : undefined}
              sx={{
                position: "absolute", left: `${left}%`, top: `${top}%`,
                transform: `translate(-50%, -50%) rotate(${g.rot || 0}deg) scale(${sx}, ${sy})`,
                fontSize: `${fontPx}px`, fontWeight: 700, lineHeight: 1, color,
                fontFamily: "Arial, sans-serif", pointerEvents: editable ? "auto" : "none",
                // grab/grabbing: the letter is draggable, which is the only way to place it precisely.
                cursor: editable ? "grab" : "default",
                "&:active": editable ? { cursor: "grabbing" } : undefined,
                touchAction: editable ? "none" : undefined, // let the pointer drag win over page scroll
                userSelect: "none", whiteSpace: "nowrap", px: 0.25,
                outline: isSel ? "2px solid rgba(25,118,210,0.7)" : "none", outlineOffset: "2px", borderRadius: 1,
              }}>
              {(g.char || "").toUpperCase() || (editable ? "?" : "")}
            </Box>
          );
        });
      })()}

      {/* Glyph toolbar — edits the SELECTED letter: type it, rotate 90°, flip, delete.
          It is anchored beside the letter it edits (not parked at the bottom of the canvas), and it only
          exists while a letter is selected — click empty canvas and it disappears. Near the right/bottom
          edge it flips to the other side so it never runs off the figure. */}
      {isGlyph && !readOnly && onChange && activeGlyph != null && (() => {
        const g = placedGlyphs[activeGlyph];
        // Rotation/mirror only change how a symbol looks if it isn't already symmetric under them —
        // a + or ○ reads identically no matter how it's turned, so offering ⟳/⇋/⤢ on those just adds
        // clutter with no visible effect. Restrict the buttons to symbols where they actually do something.
        const canOrient = g && !ROTATION_INVARIANT_CHARS.has(String(g.char || "").trim());
        const gx = (g?.pos?.[0] ?? 0.5) * 100;
        const gy = (g?.pos?.[1] ?? 0.5) * 100;
        const flipLeft = gx > 62;   // too close to the right edge — hang the bar to the left instead
        const above = gy > 62;      // too close to the bottom — sit above the letter instead
        const btn = { minWidth: 0, width: 32, height: 32, p: 0, fontSize: "1.1rem", lineHeight: 1, borderRadius: 1, border: "1px solid", borderColor: "rgba(25,118,210,0.35)", bgcolor: "#fff", cursor: "pointer", color: ACTIVE_STROKE, display: "flex", alignItems: "center", justifyContent: "center" };
        const btnOn = { ...btn, bgcolor: "rgba(25,118,210,0.14)", borderColor: ACTIVE_STROKE };
        return (
          <Box
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: "absolute",
              left: `${gx}%`,
              top: `${gy}%`,
              transform: `translate(${flipLeft ? "-100%" : "0%"}, ${above ? "-140%" : "40%"})`,
              display: "flex", gap: 0.5, alignItems: "center", zIndex: 7,
              bgcolor: "rgba(255,255,255,0.98)", border: "1px solid", borderColor: ACTIVE_STROKE, borderRadius: 1.5, p: 0.5,
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            }}
          >
            {/* Symbol-palette questions (e.g. reflect/rotate a tile of +, ○, △, ↔) let the student tap a
                fixed symbol instead of typing — some symbols aren't on a keyboard. Falls back to the free
                text input when no palette is set. */}
            {Array.isArray(spec?.symbol_palette) && spec.symbol_palette.length > 0 ? (
              <Box sx={{ display: "flex", gap: 0.25 }}>
                {spec.symbol_palette.map((sym) => (
                  <Box key={sym} component="button" type="button" title={`Place ${sym}`}
                    onClick={() => updateGlyph(activeGlyph, { char: String(sym) })}
                    sx={g.char === String(sym) ? btnOn : btn}>{sym}</Box>
                ))}
              </Box>
            ) : (
              <Box component="input" autoFocus value={g.char || ""} maxLength={2} placeholder="?"
                onChange={(e) => updateGlyph(activeGlyph, { char: e.target.value.toUpperCase().slice(0, 2) })}
                // autoFocus puts the caret here the moment a letter is selected, so you can just type. That
                // takes focus off the glyph, so the arrow-key nudge has to work from the input too —
                // otherwise selecting a letter would silently disable moving it with the keyboard.
                onKeyDown={(e) => {
                  const step = e.shiftKey ? 4 : 1;
                  const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
                  if (d) {
                    e.preventDefault();
                    nudgeGlyph(activeGlyph, d[0], d[1]);
                    return;
                  }
                  if (e.key === "Escape") setSelGlyph(null);
                }}
                sx={{ width: 38, height: 32, textAlign: "center", fontWeight: 700, border: "1px solid", borderColor: "rgba(25,118,210,0.35)", borderRadius: 1, outline: "none", fontSize: "1.1rem", color: ACTIVE_STROKE, bgcolor: "#fff" }} />
            )}
            {canOrient && (
              <>
                <Box component="button" type="button" title="Rotate 90°" onClick={() => updateGlyph(activeGlyph, { rot: ((g.rot || 0) + 90) % 360 })} sx={btn}>⟳</Box>
                {/* The two flips are MUTUALLY EXCLUSIVE. Applying both is not a mirror image at all — it is a
                    180° rotation — but for symmetric letters (O, W) it looks identical to a single flip, so a
                    student who pressed both produced a picture indistinguishable from the model answer and was
                    still marked wrong, with nothing on screen to explain why. Turning one flip on now turns the
                    other off, so "flipped" always means a genuine reflection. Use ⟳ to rotate. */}
                <Box component="button" type="button" title="Mirror left-right (a reflection in a vertical line)"
                  onClick={() => updateGlyph(activeGlyph, { flipH: !g.flipH, flipV: false })}
                  sx={g.flipH ? btnOn : btn}>⇋</Box>
                <Box component="button" type="button" title="Mirror up-down (a reflection in a horizontal line)"
                  onClick={() => updateGlyph(activeGlyph, { flipV: !g.flipV, flipH: false })}
                  sx={g.flipV ? btnOn : btn}>⤢</Box>
              </>
            )}
            <Box component="button" type="button" title="Remove this letter" onClick={() => removeGlyph(activeGlyph)} sx={{ ...btn, color: "#c62828", borderColor: "rgba(198,40,40,0.4)" }}>✕</Box>
          </Box>
        );
      })()}
      </Box>

      {/* Multi-group shade picker — one swatch per category. Tap a swatch to make it active, then tap
          the chart's regions to colour them in that category. The running count is shown so the student
          can check they've coloured as many regions as the data table calls for (the count itself is
          never given — they work it out). Lives outside the image wrapper so it lays out in normal flow. */}
      {isGroupShade && !readOnly && onChange && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
            Pick an activity, then tap the chart to colour it in:
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75 }}>
            {shadeGroups.map((g) => {
              const key = String(g.key);
              const c = groupColorByKey[key];
              const active = currentGroup === key;
              const n = Object.values(groupOfRegion).filter((v) => v === key).length;
              return (
                <Box
                  key={key}
                  onClick={() => setActiveGroup(key)}
                  sx={{
                    display: "flex", alignItems: "center", gap: 0.75,
                    px: 1.25, py: 0.5, borderRadius: 1, cursor: "pointer", userSelect: "none",
                    fontSize: "0.8rem", fontWeight: 700, color: c.stroke,
                    bgcolor: active ? c.fill : "transparent",
                    border: active ? "2px solid" : "1.5px solid",
                    borderColor: c.stroke,
                    boxShadow: active ? `0 0 0 2px ${c.fill}` : "none",
                  }}
                >
                  <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: c.fill, border: "1px solid", borderColor: c.stroke }} />
                  {g.label || key}
                  {n > 0 && (
                    <Box component="span" sx={{ fontWeight: 600, opacity: 0.85 }}>({n})</Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Draggable label chips (label_only shade questions) — a word bank of the candidate captions.
          Drag one onto a part (its label box, or the part itself) to caption it; typing in the box
          still works. Lives OUTSIDE the image wrapper (which is position:relative / line-height:0 and
          hosts only absolutely-positioned overlays) so it lays out in normal flow beneath the diagram.
          A chip dims once its text is in use, but stays draggable so it can be moved to another part. */}
      {labelOnly && !readOnly && onChange && (() => {
        const chipLabels = labelOptions;
        if (chipLabels.length === 0) return null;
        const usedLabels = Object.values(shadeLabels).map((v) => String(v || "").trim().toLowerCase());
        return (
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", mr: 0.5 }}>
              Drag a label onto a part:
            </Typography>
            {chipLabels.map((lbl) => {
              const used = usedLabels.includes(String(lbl).trim().toLowerCase());
              return (
                <Box
                  key={lbl}
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData("text/plain", lbl)}
                  sx={{
                    px: 1.25, py: 0.5, borderRadius: 4, cursor: "grab", userSelect: "none",
                    fontSize: "0.8rem", fontWeight: 700,
                    color: used ? "text.disabled" : ACTIVE_STROKE,
                    bgcolor: used ? "rgba(0,0,0,0.04)" : "rgba(25,118,210,0.10)",
                    border: "1.5px solid",
                    borderColor: used ? "divider" : ACTIVE_STROKE,
                    opacity: used ? 0.55 : 1,
                  }}
                >
                  {lbl}
                </Box>
              );
            })}
          </Box>
        );
      })()}

      {/* Forced label prompt — graded-label plot questions open this on placing/tapping a point so the
          student can't leave a correctly-plotted point unlabelled (the on-grid inline box was missed).
          A blank confirm on a just-placed point removes it; on an existing point it keeps the point. */}
      {labelRequired && labelPrompt && !readOnly && onChange && (() => {
        const commit = (raw) => {
          const label = (raw || "").trim();
          // Only the points the spec actually names are graded on their label (e.g. "plot D and draw the
          // parallelogram ABCD" names D alone — A/B/C are corners of the shape, not answers). Dropping a
          // corner just because the student skipped its label would make the shape impossible to close,
          // so a blank label only discards the point when EVERY point of this question is named.
          const namedCount = Array.isArray(pointLabels) ? pointLabels.filter(Boolean).length : 0;
          const everyPointNamed = namedCount > 0 && pointCount > 0 && namedCount >= pointCount;
          if (labelPrompt.isNew && label === "" && everyPointNamed) {
            emitValue(placedPts.filter((_, i) => i !== labelPrompt.index), edgesAvailable ? manualEdges : undefined);
          } else {
            setPointLabel(labelPrompt.index, label);
          }
          setLabelPrompt(null);
        };
        const remove = () => {
          emitValue(placedPts.filter((_, i) => i !== labelPrompt.index), edgesAvailable ? manualEdges : undefined);
          setLabelPrompt(null);
        };
        const expected = Array.isArray(pointLabels) ? pointLabels.filter(Boolean) : [];
        const allNamed = expected.length > 0 && pointCount > 0 && expected.length >= pointCount;
        return (
          <Dialog open onClose={() => commit(labelPrompt.value)} maxWidth="xs" fullWidth>
            <DialogTitle>Label this point</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {expected.length
                  ? `Type the letter that names this point (this question labels: ${expected.join(", ")}). The label is part of the answer — a point on its own isn’t enough.`
                  : "Type the letter that names this point. It’s part of the answer — a point on its own isn’t enough."}
                {!allNamed && " Corners that aren’t named can be left blank — just tap Skip."}
              </Typography>
              <TextField
                autoFocus
                fullWidth
                size="small"
                label="Point label"
                value={labelPrompt.value}
                onChange={(e) => setLabelPrompt({ ...labelPrompt, value: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(labelPrompt.value); } }}
                inputProps={{ maxLength: 12 }}
              />
            </DialogContent>
            <DialogActions>
              <Button color="error" onClick={remove}>Remove point</Button>
              {!allNamed && (
                <Button onClick={() => commit("")}>Skip</Button>
              )}
              <Button variant="contained" onClick={() => commit(labelPrompt.value)}>Save label</Button>
            </DialogActions>
          </Dialog>
        );
      })()}

      {/* Forced shape-name prompt — "Label the triangle A" / "Label your shape B". Opens when the shape
          is complete (all corners placed, or Fill in) so the student can't leave a correct drawing
          unlabelled. Same modal pattern as the per-point label prompt above. */}
      {requireShapeLabel && shapeLabelPrompt && !readOnly && onChange && (
        <Dialog open onClose={() => { if ((shapeLabelPrompt.value || "").trim()) { setShapeLabel(shapeLabelPrompt.value); setShapeLabelPrompt(null); } }} maxWidth="xs" fullWidth>
          <DialogTitle>Label this shape</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Type the letter that names this shape (for example A, B or C). The label is part of the answer — drawing the shape alone isn’t enough.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Shape label"
              value={shapeLabelPrompt.value}
              onChange={(e) => setShapeLabelPrompt({ ...shapeLabelPrompt, value: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  const label = (shapeLabelPrompt.value || "").trim();
                  if (!label) return;
                  setShapeLabel(label);
                  setShapeLabelPrompt(null);
                }
              }}
              inputProps={{ maxLength: 8 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShapeLabelPrompt(null)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={!(shapeLabelPrompt.value || "").trim()}
              onClick={() => {
                setShapeLabel(shapeLabelPrompt.value);
                setShapeLabelPrompt(null);
              }}
            >
              Save label
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Forced letter-entry prompt — "write C in this section". Opens when the student taps a
          letter-entry cell's shape (see toggleRegion), instead of an always-visible floating input
          box that was too easy to mis-attribute to the wrong region in a crowded figure. */}
      {letterPrompt && !readOnly && onChange && (
        <Dialog open onClose={() => setLetterPrompt(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Label this shape</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Type the letter that goes in this section. The label is part of the answer.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Letter"
              value={letterPrompt.value}
              onChange={(e) => setLetterPrompt({ ...letterPrompt, value: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  setRegionLabel(letterPrompt.id, letterPrompt.value);
                  setLetterPrompt(null);
                }
              }}
              inputProps={{ maxLength: 4 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLetterPrompt(null)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => {
                setRegionLabel(letterPrompt.id, letterPrompt.value);
                setLetterPrompt(null);
              }}
            >
              Save letter
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default InteractiveDiagramCanvas;
